/**
 * Marriage Application Detail Page
 * Displays detailed information for a selected marriage certificate application
 */

import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { serviceChargeService } from "../services/api/serviceCharge.service";
import type { ServiceCharge } from "../types/serviceCharge.types";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { applicantService } from "../services/api/applicant.service";
import { paymentService } from "../services/api/payment.service";
import { officiatorService } from "../services/api/officiator.service";
import { currentSpouseService } from "../services/api/currentSpouse.service";
import { exSpouseService } from "../services/api/exSpouse.service";
import { settingsService } from "../services/api/settings.service";
import { notificationService } from "../services/api/notification.service";
import { workflowService } from "../services/api/workflow.service";
import type { QueryWorkflowActionDto, Workflow } from "../types/workflow.types";
import type {
  CertificateApplication,
  UpdateCertificateApplicationDto,
  County,
  Nationality,
} from "../types/certificate.types";
import type {
  Applicant,
  CreateApplicantDto,
  UpdateApplicantDto,
} from "../types/applicant.types";
import type {
  Officiator,
  CreateOfficiatorDto,
  UpdateOfficiatorDto,
} from "../types/officiator.types";
import type {
  CurrentSpouse,
  CreateCurrentSpouseDto,
} from "../types/currentSpouse.types";
import type { ExSpouse, CreateExSpouseDto } from "../types/exSpouse.types";
import type { RequiredDocumentByModuleItem } from "../types/requiredDocument.types";
import { apiRequest } from "../api/apiClient";
import { ApplicationCategory, CountyEnum } from "../types/certificate.types";
import { ApplicantType } from "../types/applicant.types";
import { UserStatusId } from "../types/user.types";
import { requiredDocumentService } from "../services/api/requiredDocument.service";
import BusyOverlay from "../components/common/BusyOverlay";
import Sidebar from "../components/Sidebar";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Footer from "../components/Footer";
import {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showDeleteConfirm,
  showConfirm,
} from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import { sanitizePhoneNumberInput, validatePhoneNumber } from "../utils/phone";
import "../styles/pages/UserDetail.css";
import "../styles/pages/Users.css";
import { CardPayment } from "./CardPayment";
import { ManualPayment } from "./ManualPayment";
import { DocumentScan } from "./DocumentScan";
import { SignatureExtraction } from "./SignatureExtraction";
import { CountyMarriageCert } from "../components/CountyMarriageCert";
import History from "../components/common/History";
import QueryRemarks from "../components/common/QueryRemarks";
import QueryApplicationForm from "../components/common/QueryApplicationForm";
import { getFullPath } from "../utils/getFullPath";
import { processPassportPhoto } from "../utils/image-processing/passportPhotoHelper";

// Default nationality constant
const DefaultNationality = "Liberian";
const BasicRole = 2;
const MarriageModuleId = 8;

interface MarriageAttachmentRequirement {
  id: number | null;
  requiredDocumentId: number;
  documentTypeId: number;
  documentTypeName: string;
  isRequired: boolean;
  isUploaded: boolean;
  documentPath: string | null;
  fileName: string;
  description: string;
}

const parseNumberOrNull = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const normalizeRequiredDocumentAttachment = (
  item: RequiredDocumentByModuleItem,
  index: number,
): MarriageAttachmentRequirement => {
  const attachment = item.attachment;
  const requiredDocumentId =
    parseNumberOrNull(item.requiredDocumentId) ??
    parseNumberOrNull(item.id) ??
    -(index + 1);
  // DELETE /Attachments/{id} must use the attachment record id from API response.
  const id =
    parseNumberOrNull(item.attachmentId) ?? parseNumberOrNull(attachment?.id);
  const documentTypeId = parseNumberOrNull(item.documentTypeId) ?? 0;
  const documentPathValue =
    item.documentPath ?? attachment?.documentPath ?? null;
  const documentPath =
    typeof documentPathValue === "string" && documentPathValue.trim().length > 0
      ? documentPathValue.trim()
      : null;
  const fileName = String(item.fileName ?? attachment?.fileName ?? "").trim();
  const description = String(
    item.description ?? attachment?.description ?? "",
  ).trim();

  const explicitUploadFlag = item.isUploaded;
  const isUploaded =
    typeof explicitUploadFlag === "boolean"
      ? explicitUploadFlag
      : Boolean(id || documentPath);

  return {
    id,
    requiredDocumentId,
    documentTypeId,
    documentTypeName: String(item.documentTypeName ?? "-").trim() || "-",
    isRequired: Boolean(item.isRequired),
    isUploaded,
    documentPath,
    fileName,
    description,
  };
};

// Calculate the latest allowed birth date for a given minimum age
// (i.e. the date exactly `minAge` years before today). Used as the `max`
// attribute on Date of Birth inputs so applicants must be at least `minAge`.
const getMaxDateForAge = (minAge: number): string => {
  const safeAge = Number.isFinite(minAge) && minAge > 0 ? minAge : 18;
  const today = new Date();
  const cutoff = new Date(
    today.getFullYear() - safeAge,
    today.getMonth(),
    today.getDate(),
  );
  return cutoff.toISOString().split("T")[0];
};

export const WeddingApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Determine back navigation path based on current URL
  const getBackPath = () => {
    if (location.pathname.includes("/print/")) {
      return "/marriages/print";
    }
    if (location.pathname.includes("/finalize/")) {
      return "/marriages/finalize";
    }
    return "/marriages/apply";
  };

  // Check if we're on the print page
  const isPrintPage = location.pathname.includes("/print/");

  // Check if we're on the finalize page
  const isFinalizePage = location.pathname.includes("/finalize/");

  // Self-service portal mode: render without the admin Topbar/Sidebar/Footer.
  const isPortal = location.pathname.startsWith("/portal");

  const [application, setApplication] = useState<CertificateApplication | null>(
    null,
  );
  const [counties, setCounties] = useState<County[]>([]);
  const [nationalities, setNationalities] = useState<Nationality[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [ministryName, setMinistryName] = useState<string>(
    "Ministry of Local Government",
  );
  const [declarationText, setDeclarationText] = useState<string>("");
  const [groomDeclarationChecked, setGroomDeclarationChecked] = useState(false);
  const [brideDeclarationChecked, setBrideDeclarationChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Workflow approval state
  const [isAtMyApprovalLevel, setIsAtMyApprovalLevel] = useState(false);
  const [currentStageOrder, setCurrentStageOrder] = useState(0);

  // Signed certificate state
  const [signedCertificate, setSignedCertificate] = useState<any>(null);
  const [signedCertLoading, setSignedCertLoading] = useState(false);

  // Certificate signatures state
  const [certificateSignatures, setCertificateSignatures] = useState<any[]>([]);

  // Signature extraction modal state
  const [showSignatureExtractionModal, setShowSignatureExtractionModal] =
    useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showQueryRemarks, setShowQueryRemarks] = useState(false);
  const [hasQueryRemarks, setHasQueryRemarks] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);

  // Accordion state
  const [groomAccordionOpen, setGroomAccordionOpen] = useState(false);
  const [currentSpousesAccordionOpen, setCurrentSpousesAccordionOpen] =
    useState(false);
  const [brideAccordionOpen, setBrideAccordionOpen] = useState(false);
  const [officiatorAccordionOpen, setOfficiatorAccordionOpen] = useState(false);
  const [attachmentsAccordionOpen, setAttachmentsAccordionOpen] =
    useState(false);

  // Payment dropdown state
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [hasExistingPayment, setHasExistingPayment] = useState(false);

  // Submission readiness state
  const [isReadyForSubmission, setIsReadyForSubmission] = useState(false);
  const [submissionRequirements, setSubmissionRequirements] = useState<
    Record<string, boolean>
  >({});

  // Service charge state for edit modal (for Marriage applications)
  const [editServiceCharge, setEditServiceCharge] =
    useState<ServiceCharge | null>(null);

  // Manual payment modal state
  const [showManualPaymentModal, setShowManualPaymentModal] = useState(false);
  const [showCardPaymentModal, setShowCardPaymentModal] = useState(false);

  // Document scan modal state
  const [showDocumentScanModal, setShowDocumentScanModal] = useState(false);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<
    number | null
  >(null);

  // Attachments state
  const [attachments, setAttachments] = useState<
    MarriageAttachmentRequirement[]
  >([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [viewingAttachmentId, setViewingAttachmentId] = useState<number | null>(
    null,
  );

  // Groom applicant state
  const [groomApplicant, setGroomApplicant] = useState<Applicant | null>(null);
  const [groomFormData, setGroomFormData] = useState<CreateApplicantDto>({
    userId: undefined,
    firstName: "",
    middleName: "",
    lastName: "",
    genderId: 1,
    dateOfBirth: "",
    nationalIdNumber: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    certificateApplicationId: 0,
    applicantType: "Groom",
    countyOfOriginId: 0,
    nationalityId: 0,
    birthCountyId: 0,
    birthTown: "",
    currentAddress: "",
    wasPreviouslyMarried: false,
    previousMarriageDate: "",
    previousSpouseName: "",
    fatherName: "",
    motherName: "",
  });
  const [groomLoading, setGroomLoading] = useState(false);
  const [groomValidationErrors, setGroomValidationErrors] = useState<
    Record<string, string>
  >({});
  const [photoProcessing, setPhotoProcessing] = useState(false);
  const [groomPhotoFile, setGroomPhotoFile] = useState<File | null>(null);
  const [groomPhotoPreview, setGroomPhotoPreview] = useState<string | null>(
    null,
  );

  // Bride applicant state
  const [brideApplicant, setBrideApplicant] = useState<Applicant | null>(null);
  const [brideFormData, setBrideFormData] = useState<CreateApplicantDto>({
    userId: undefined,
    firstName: "",
    middleName: "",
    lastName: "",
    genderId: 2,
    dateOfBirth: "",
    nationalIdNumber: "",
    email: "",
    primaryPhone: "",
    secondaryPhone: "",
    certificateApplicationId: 0,
    applicantType: "Bride",
    countyOfOriginId: 0,
    nationalityId: 0,
    birthCountyId: 0,
    birthTown: "",
    currentAddress: "",
    wasPreviouslyMarried: false,
    previousMarriageDate: "",
    previousSpouseName: "",
    fatherName: "",
    motherName: "",
    brideProposedName: "",
  });
  const [brideLoading, setBrideLoading] = useState(false);
  const [brideValidationErrors, setBrideValidationErrors] = useState<
    Record<string, string>
  >({});
  const [bridePhotoFile, setBridePhotoFile] = useState<File | null>(null);
  const [bridePhotoPreview, setBridePhotoPreview] = useState<string | null>(
    null,
  );

  // Officiator state
  const [officiator, setOfficiator] = useState<Officiator | null>(null);
  const [officiatorFormData, setOfficiatorFormData] =
    useState<CreateOfficiatorDto>({
      certificateApplicationId: 0,
      officiatorName: "",
      officiatorContact: "",
      witnessName: "",
      witnessContact: "",
    });
  const [officiatorLoading, setOfficiatorLoading] = useState(false);
  const [officiatorValidationErrors, setOfficiatorValidationErrors] = useState<
    Record<string, string>
  >({});

  // Ex-spouses state
  const [exSpousesAccordionOpen, setExSpousesAccordionOpen] = useState(false);
  const [brideExSpousesAccordionOpen, setBrideExSpousesAccordionOpen] =
    useState(false);
  const [exSpouses, setExSpouses] = useState<ExSpouse[]>([]);
  const [brideExSpouses, setBrideExSpouses] = useState<ExSpouse[]>([]);
  const [exSpouseFormData, setExSpouseFormData] = useState<CreateExSpouseDto>({
    certificateApplicationId: 0,
    isGroomEx: true,
    spouseName: "",
    primaryPhone: "",
    secondaryPhone: "",
    email: "",
    address: "",
  });
  const [exSpouseLoading, setExSpouseLoading] = useState(false);
  const [exSpouseSubmitting, setExSpouseSubmitting] = useState(false);
  const [showExSpouseForm, setShowExSpouseForm] = useState(false);
  const [editingExSpouseId, setEditingExSpouseId] = useState<number | null>(
    null,
  );
  const [exSpouseValidationErrors, setExSpouseValidationErrors] = useState<
    Record<string, string>
  >({});
  const [exSpousePhotoFile, setExSpousePhotoFile] = useState<File | null>(null);
  const [exSpousePhotoPreview, setExSpousePhotoPreview] = useState<
    string | null
  >(null);

  // Current spouses state
  const [currentSpouses, setCurrentSpouses] = useState<CurrentSpouse[]>([]);
  const [currentSpouseFormData, setCurrentSpouseFormData] =
    useState<CreateCurrentSpouseDto>({
      certificateApplicationId: 0,
      spouseName: "",
      primaryPhone: "",
      secondaryPhone: "",
      email: "",
      address: "",
    });
  const [currentSpouseLoading, setCurrentSpouseLoading] = useState(false);
  const [currentSpouseSubmitting, setCurrentSpouseSubmitting] = useState(false);
  const [showCurrentSpouseForm, setShowCurrentSpouseForm] = useState(false);
  const [editingCurrentSpouseId, setEditingCurrentSpouseId] = useState<
    number | null
  >(null);
  const [currentSpouseValidationErrors, setCurrentSpouseValidationErrors] =
    useState<Record<string, string>>({});
  const [currentSpousePhotoFile, setCurrentSpousePhotoFile] =
    useState<File | null>(null);
  const [currentSpousePhotoPreview, setCurrentSpousePhotoPreview] = useState<
    string | null
  >(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] =
    useState<UpdateCertificateApplicationDto>({});
  const [editLoading, setEditLoading] = useState(false);
  const [isRoleCountyLinked, setIsRoleCountyLinked] = useState(false);
  const [linkedCountyId, setLinkedCountyId] = useState<number | null>(null);

  // Photo processing settings
  const [enableBackgroundRemoval, setEnableBackgroundRemoval] = useState(true);

  // Minimum ages (from Settings) used to constrain Date of Birth. Defaults
  // mirror the previous hard-coded values (groom 21, bride 18).
  const [groomMinAge, setGroomMinAge] = useState(21);
  const [brideMinAge, setBrideMinAge] = useState(18);

  const hasRequiredPaymentDetails = Boolean(
    groomApplicant && brideApplicant && officiator,
  );
  const hasAttachmentsAddedRequirement = Object.entries(
    submissionRequirements,
  ).some(([key, value]) => {
    const normalizedKey = key.replace(/[^a-z]/gi, "").toLowerCase();
    return (
      (normalizedKey === "attachmentsadded" ||
        normalizedKey === "attachmentadded") &&
      value === true
    );
  });
  const canManageGroomSpouses = Boolean(groomApplicant);
  const canManageBrideSpouses = Boolean(brideApplicant);
  const [editError, setEditError] = useState("");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Application Detail", "View application information");
    fetchData();
    fetchCounties();
    fetchNationalities();
    fetchSettings();
    fetchWorkflows();
    if (id) {
      fetchApplicants();
      fetchOfficiator();
      fetchExSpouses();
      fetchCurrentSpouses();
      fetchAttachments();
      if (isFinalizePage) {
        fetchSignedCertificate();
        fetchCertificateSignatures();
      }
    }
  }, [id, setPageTitle]);

  const checkRoleCountyLink = async () => {
    try {
      const response = await apiRequest("/RoleCounties/is-linked", {
        method: "GET",
      });

      if (!response.ok) {
        setIsRoleCountyLinked(false);
        setLinkedCountyId(null);
        return;
      }

      const payload = (await response.json().catch(() => null)) as {
        countyId?: number | null;
        data?: { countyId?: number | null };
      } | null;

      const source = payload?.data ?? payload;
      const countyCandidate = Number(source?.countyId);

      if (Number.isFinite(countyCandidate) && countyCandidate > 0) {
        setIsRoleCountyLinked(true);
        setLinkedCountyId(countyCandidate);
        return;
      }

      setIsRoleCountyLinked(false);
      setLinkedCountyId(null);
    } catch {
      setIsRoleCountyLinked(false);
      setLinkedCountyId(null);
    }
  };

  useEffect(() => {
    checkRoleCountyLink();
  }, []);

  // Fetch photo processing settings
  useEffect(() => {
    const fetchPhotoSettings = async () => {
      try {
        const setting = await settingsService.getSettingByKey(
          "EnableBackgroundRemoval",
        );
        if (setting && setting.value !== undefined) {
          setEnableBackgroundRemoval(setting.value === "true");
        }
      } catch (err) {
        console.error("Error fetching photo settings:", err);
        // Use default value (true) if fetch fails
        setEnableBackgroundRemoval(true);
      }
    };
    fetchPhotoSettings();
  }, []);

  // Fetch minimum-age settings for groom and bride
  useEffect(() => {
    const fetchMinAges = async () => {
      try {
        const [groomSetting, brideSetting] = await Promise.all([
          settingsService.getSettingByKey("GroomMinimumAge"),
          settingsService.getSettingByKey("BrideMinimumAge"),
        ]);
        const groomVal = parseInt(groomSetting?.value ?? "", 10);
        const brideVal = parseInt(brideSetting?.value ?? "", 10);
        if (Number.isFinite(groomVal) && groomVal > 0) setGroomMinAge(groomVal);
        if (Number.isFinite(brideVal) && brideVal > 0) setBrideMinAge(brideVal);
      } catch (err) {
        console.error("Error fetching minimum age settings:", err);
        // Keep defaults (groom 21, bride 18) on failure.
      }
    };
    fetchMinAges();
  }, []);

  useEffect(() => {
    const loadQueryRemarks = async () => {
      if (!application?.id) {
        setHasQueryRemarks(false);
        return;
      }

      try {
        const remarks = await workflowService.getRequeryRemarks(
          "CertificateApplications",
          application.id,
        );
        setHasQueryRemarks(remarks.length > 0);
      } catch {
        setHasQueryRemarks(false);
      }
    };

    loadQueryRemarks();
  }, [application?.id]);

  // Auto-select workflow based on processAtHeadquarters
  useEffect(() => {
    if (
      workflows.length > 0 &&
      editFormData.processAtHeadquarters !== undefined
    ) {
      const selectedWorkflow = editFormData.processAtHeadquarters
        ? workflows.find((w) => w.name.toLowerCase().includes("headquarters"))
        : workflows.find((w) => w.name.toLowerCase().includes("county"));

      if (selectedWorkflow && selectedWorkflow.id !== editFormData.workflowId) {
        setEditFormData((prev) => ({
          ...prev,
          workflowId: selectedWorkflow.id,
        }));
      }
    }
  }, [workflows, editFormData.processAtHeadquarters]);

  // Close payment dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        paymentDropdownOpen &&
        !target.closest(".user-detail-action-btn--warning")
      ) {
        setPaymentDropdownOpen(false);
      }
    };

    if (paymentDropdownOpen) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [paymentDropdownOpen]);

  // Fetch Currency and ServiceCharge from /ServiceCharges/category/1 for edit modal
  useEffect(() => {
    if (showEditModal) {
      const fetchPrice = async () => {
        try {
          const weddingPrice =
            await serviceChargeService.getServiceChargeByCategoryId(1);
          setEditServiceCharge(weddingPrice);
        } catch {
          setEditServiceCharge(null);
        }
      };
      fetchPrice();
    } else {
      setEditServiceCharge(null);
    }
  }, [showEditModal]);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await certificateService.getCertificateApplicationById(
        parseInt(id, 10),
      );
      setApplication(data);

      // Check if workflow is at user's approval level and get current stage
      if (data.workflowId) {
        try {
          const [atMyLevel, currentStage] = await Promise.all([
            workflowService.isAtMyApprovalLevel(data.workflowId, data.id!),
            workflowService.getCurrentStageRole(data.workflowId, data.id!),
          ]);
          setIsAtMyApprovalLevel(atMyLevel);
          setCurrentStageOrder(currentStage.stageOrder);
        } catch (err) {
          console.error("Error checking approval level:", err);
          setIsAtMyApprovalLevel(false);
          setCurrentStageOrder(0);
        }
      } else {
        setIsAtMyApprovalLevel(false);
        setCurrentStageOrder(0);
      }

      // Check for existing payments
      await checkExistingPayments(parseInt(id, 10));

      // Check if ready for submission
      await checkReadyForSubmission(parseInt(id, 10));
    } catch (err: any) {
      console.error("Error fetching application:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingPayments = async (applicationId: number) => {
    try {
      const payments =
        await paymentService.getPaymentsByApplicationId(applicationId);
      setHasExistingPayment(payments && payments.length > 0);
    } catch (err: any) {
      console.error("Error checking payments:", err);
      // Don't show error to user, just assume no payments
      setHasExistingPayment(false);
    }
  };

  const checkReadyForSubmission = async (applicationId: number) => {
    try {
      const readinessChecks = await certificateService.checkReadyForSubmission(
        applicationId,
        ModuleId.Weddings,
      );
      // Store the full requirements object
      setSubmissionRequirements(readinessChecks);
      // Check if all values are true
      const allReady = Object.values(readinessChecks).every(
        (value) => value === true,
      );
      setIsReadyForSubmission(allReady);
    } catch (err: any) {
      console.error("Error checking submission readiness:", err);
      // Don't show error to user, just assume not ready
      setIsReadyForSubmission(false);
      setSubmissionRequirements({});
    }
  };

  const fetchCounties = async () => {
    try {
      const data = await certificateService.getCounties();
      setCounties(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching counties:", err);
    }
  };

  const fetchNationalities = async () => {
    try {
      const data = await certificateService.getNationalities();
      setNationalities(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching nationalities:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const settings = await settingsService.getSettings();
      const normalizeKey = (key: string) =>
        key && key.length > 0 ? key[0].toLowerCase() + key.slice(1) : key;

      const ministrySetting = settings.find(
        (s) =>
          normalizeKey(s.key) === "ministryName" && s.category === "general",
      );
      if (ministrySetting?.value) {
        setMinistryName(ministrySetting.value);
      }

      const declarationSetting = settings.find(
        (s) =>
          normalizeKey(s.key) === "declaration" && s.category === "general",
      );
      setDeclarationText(declarationSetting?.value?.trim() || "");
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      // Keep default value on error
    }
  };

  const fetchWorkflows = async () => {
    try {
      const data = await workflowService.getWorkflowsByModule(
        ModuleId.Weddings,
      );
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching workflows:", err);
    }
  };

  const fetchAttachments = async () => {
    if (!id) return;

    try {
      setAttachmentsLoading(true);
      const data = await requiredDocumentService.getRequiredDocumentsByModule(
        MarriageModuleId,
        Number(id),
      );
      const requiredDocs = Array.isArray(data) ? data : [];
      setAttachments(
        requiredDocs.map((doc, index) =>
          normalizeRequiredDocumentAttachment(doc, index),
        ),
      );
    } catch (err: any) {
      console.error("Error fetching attachments:", err);
      // Don't show error toast, just log it
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const fetchCertificateSignatures = async () => {
    if (!id) return;
    try {
      const sigs =
        await certificateService.getCertificateSignaturesByApplicationId(
          Number(id),
        );
      setCertificateSignatures(Array.isArray(sigs) ? sigs : []);
    } catch {
      setCertificateSignatures([]);
    }
  };

  const fetchSignedCertificate = async () => {
    if (!id) return;

    try {
      setSignedCertLoading(true);
      const response = await apiRequest(
        `/SignedCertificates/by-application/${id}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        // Don't throw error, just set null if not found
        setSignedCertificate(null);
        return;
      }

      const data = await response.json();
      // API returns an array, get the first item
      const certArray = Array.isArray(data) ? data : [];
      setSignedCertificate(certArray.length > 0 ? certArray[0] : null);
    } catch (err: any) {
      console.error("Error fetching signed certificate:", err);
      setSignedCertificate(null);
    } finally {
      setSignedCertLoading(false);
    }
  };

  const handleDeleteAttachment = async (
    attachment: MarriageAttachmentRequirement,
  ) => {
    if (!attachment.id) {
      showWarningToast("No attachment record found for this required document");
      return;
    }

    const confirmed = await Swal.fire({
      title: "Delete attachment?",
      html: `Are you sure you want to delete <strong>${attachment.fileName || attachment.documentTypeName}</strong>?<br/>This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    }).then((result) => result.isConfirmed);

    if (!confirmed) return;

    try {
      setAttachmentsLoading(true);
      const response = await apiRequest(`/Attachments/${attachment.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete attachment");
      }

      showSuccessToast("Attachment deleted successfully");
      if (viewingAttachmentId === attachment.requiredDocumentId) {
        setViewingAttachmentId(null);
      }
      await fetchAttachments();
      if (id) {
        await checkReadyForSubmission(Number(id));
      }
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const handleDeleteSignedCertificate = async () => {
    if (!signedCertificate) return;

    const confirmed = await showDeleteConfirm(
      "Signed Certificate",
      "signed certificate",
    );

    if (!confirmed) return;

    try {
      setSignedCertLoading(true);
      const response = await apiRequest(
        `/SignedCertificates/${signedCertificate.id}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete signed certificate");
      }

      showSuccessToast("Signed certificate deleted successfully");
      setSignedCertificate(null);
      await fetchData();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setSignedCertLoading(false);
    }
  };

  // Set default nationality to Liberia when nationalities are loaded and no existing groom
  useEffect(() => {
    if (
      nationalities.length > 0 &&
      !groomApplicant &&
      groomFormData.nationalityId === 0
    ) {
      const liberia = nationalities.find(
        (nat) => nat.name.toLowerCase() === DefaultNationality.toLowerCase(),
      );
      if (liberia) {
        setGroomFormData((prev) => ({
          ...prev,
          nationalityId: liberia.id,
        }));
      }
    }
  }, [nationalities, groomApplicant, groomFormData.nationalityId]);

  // Set default nationality to Liberia for bride as well
  useEffect(() => {
    if (
      nationalities.length > 0 &&
      !brideApplicant &&
      brideFormData.nationalityId === 0
    ) {
      const liberia = nationalities.find(
        (nat) => nat.name.toLowerCase() === DefaultNationality.toLowerCase(),
      );
      if (liberia) {
        setBrideFormData((prev) => ({
          ...prev,
          nationalityId: liberia.id,
        }));
      }
    }
  }, [nationalities, brideApplicant, brideFormData.nationalityId]);

  const fetchOfficiator = async () => {
    if (!id) return;

    try {
      setOfficiatorLoading(true);
      const officiatorData =
        await officiatorService.getOfficiatorByCertificateApplicationId(
          Number(id),
        );

      if (officiatorData) {
        setOfficiator(officiatorData);
        setOfficiatorFormData({
          certificateApplicationId:
            officiatorData.certificateApplicationId || Number(id),
          officiatorName: officiatorData.officiatorName || "",
          officiatorContact: officiatorData.officiatorContact || "",
          witnessName: officiatorData.witnessName || "",
          witnessContact: officiatorData.witnessContact || "",
        });
        // Open accordion in edit mode when officiator record exists
        setOfficiatorAccordionOpen(true);
      } else {
        setOfficiator(null);
        setOfficiatorFormData({
          certificateApplicationId: Number(id),
          officiatorName: "",
          officiatorContact: "",
          witnessName: "",
          witnessContact: "",
        });
      }
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setOfficiatorLoading(false);
    }
  };

  const fetchExSpouses = async () => {
    if (!id) return;

    try {
      setExSpouseLoading(true);
      const spouses =
        await exSpouseService.getExSpousesByCertificateApplicationId(
          Number(id),
        );
      const allSpouses = Array.isArray(spouses) ? spouses : [];
      const spouseList = allSpouses.filter(
        (spouse) => (spouse.isGroomEx ?? spouse.IsGroomEx ?? true) === true,
      );
      const brideSpouseList = allSpouses.filter(
        (spouse) => (spouse.isGroomEx ?? spouse.IsGroomEx ?? true) === false,
      );
      setExSpouses(spouseList);
      setBrideExSpouses(brideSpouseList);
      if (spouseList.length > 0) {
        setExSpousesAccordionOpen(true);
      }
      if (brideSpouseList.length > 0) {
        setBrideExSpousesAccordionOpen(true);
      }
    } catch {
      showErrorToast(GENERIC_ERROR_MESSAGE);
      setExSpouses([]);
      setBrideExSpouses([]);
    } finally {
      setExSpouseLoading(false);
    }
  };

  const handleExSpouseFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    setExSpouseFormData((prev) => ({ ...prev, [name]: nextValue }));

    if (exSpouseValidationErrors[name]) {
      setExSpouseValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleExSpousePhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      try {
        setPhotoProcessing(true);
        const result = await processPassportPhoto(
          file,
          enableBackgroundRemoval,
        );
        setExSpousePhotoFile(result.file);
        setExSpousePhotoPreview(result.base64);
        if (exSpouseValidationErrors.photo) {
          setExSpouseValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.photo;
            return newErrors;
          });
        }
      } catch {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removeExSpousePhoto = () => {
    setExSpousePhotoFile(null);
    setExSpousePhotoPreview(null);
    setExSpouseFormData((prev) => ({ ...prev, profileImagePath: "" }));
  };

  const validateExSpouseForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (
      editingExSpouseId === null &&
      !exSpousePhotoFile &&
      !exSpousePhotoPreview
    ) {
      errors.photo = "Passport Size Photo is required";
    }

    if (!exSpouseFormData.spouseName.trim()) {
      errors.spouseName = REQUIRED_FIELD_MESSAGE;
    }

    const primaryPhoneError = validatePhoneNumber(
      exSpouseFormData.primaryPhone,
      {
        required: true,
      },
    );
    if (primaryPhoneError) {
      errors.primaryPhone = primaryPhoneError;
    }

    const secondaryPhoneError = validatePhoneNumber(
      exSpouseFormData.secondaryPhone,
    );
    if (secondaryPhoneError) {
      errors.secondaryPhone = secondaryPhoneError;
    }

    if (
      exSpouseFormData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(exSpouseFormData.email.trim())
    ) {
      errors.email = "Enter a valid email address";
    }

    if (!exSpouseFormData.address.trim()) {
      errors.address = REQUIRED_FIELD_MESSAGE;
    }

    setExSpouseValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateExSpouse = async () => {
    if (!application?.id) return;

    if (!validateExSpouseForm()) return;

    const isEditing = editingExSpouseId !== null;
    const confirmed = await showConfirm(
      isEditing ? "Update Ex-Spouse" : "Add Ex-Spouse",
      isEditing
        ? "Are you sure you want to update this ex-spouse record?"
        : "Are you sure you want to add this ex-spouse record?",
      isEditing ? "Yes, update" : "Yes, add",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setExSpouseSubmitting(true);

      if (isEditing) {
        await exSpouseService.updateExSpouse(
          {
            id: editingExSpouseId,
            ...exSpouseFormData,
            certificateApplicationId: application.id,
          },
          exSpousePhotoFile || undefined,
        );
      } else {
        await exSpouseService.createExSpouse(
          {
            ...exSpouseFormData,
            certificateApplicationId: application.id,
          },
          exSpousePhotoFile || undefined,
        );
      }

      showSuccessToast(
        isEditing
          ? "Ex-spouse updated successfully"
          : "Ex-spouse added successfully",
      );
      setExSpouseFormData({
        certificateApplicationId: application.id,
        isGroomEx: true,
        spouseName: "",
        primaryPhone: "",
        secondaryPhone: "",
        email: "",
        address: "",
        profileImagePath: "",
      });
      setExSpouseValidationErrors({});
      setEditingExSpouseId(null);
      setExSpousePhotoFile(null);
      setExSpousePhotoPreview(null);
      setShowExSpouseForm(false);
      await fetchExSpouses();
    } catch {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setExSpouseSubmitting(false);
    }
  };

  const handleEditExSpouse = (spouse: ExSpouse) => {
    setEditingExSpouseId(spouse.id);
    setExSpouseFormData({
      certificateApplicationId: spouse.certificateApplicationId,
      isGroomEx: spouse.isGroomEx ?? spouse.IsGroomEx ?? true,
      spouseName: spouse.spouseName || "",
      primaryPhone: spouse.primaryPhone || "",
      secondaryPhone: spouse.secondaryPhone || "",
      email: spouse.email || "",
      address: spouse.address || "",
      profileImagePath: spouse.profileImagePath || "",
    });
    const spousePhotoPath = spouse.photoUrl || spouse.profileImagePath;
    const spousePhotoUrl = getFullPath(spousePhotoPath);
    setExSpousePhotoFile(null);
    setExSpousePhotoPreview(spousePhotoUrl || null);
    setExSpouseValidationErrors({});
    setShowExSpouseForm(true);
  };

  const handleDeleteExSpouse = async (spouse: ExSpouse) => {
    const confirmed = await showDeleteConfirm(spouse.spouseName, "ex-spouse");

    if (!confirmed) return;

    try {
      setExSpouseSubmitting(true);
      await exSpouseService.deleteExSpouse(spouse.id);
      showSuccessToast("Ex-spouse deleted successfully");

      if (editingExSpouseId === spouse.id) {
        setEditingExSpouseId(null);
        setShowExSpouseForm(false);
        setExSpouseValidationErrors({});
      }

      await fetchExSpouses();
    } catch {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setExSpouseSubmitting(false);
    }
  };

  const fetchCurrentSpouses = async () => {
    if (!id) return;

    try {
      setCurrentSpouseLoading(true);
      const spouses =
        await currentSpouseService.getCurrentSpousesByCertificateApplicationId(
          Number(id),
        );
      const spouseList = Array.isArray(spouses) ? spouses : [];
      setCurrentSpouses(spouseList);
      if (spouseList.length > 0) {
        setCurrentSpousesAccordionOpen(true);
      }
    } catch {
      showErrorToast(GENERIC_ERROR_MESSAGE);
      setCurrentSpouses([]);
    } finally {
      setCurrentSpouseLoading(false);
    }
  };

  const handleCurrentSpouseFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    setCurrentSpouseFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    if (currentSpouseValidationErrors[name]) {
      setCurrentSpouseValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCurrentSpousePhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      try {
        setPhotoProcessing(true);
        const result = await processPassportPhoto(
          file,
          enableBackgroundRemoval,
        );
        setCurrentSpousePhotoFile(result.file);
        setCurrentSpousePhotoPreview(result.base64);
        if (currentSpouseValidationErrors.photo) {
          setCurrentSpouseValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.photo;
            return newErrors;
          });
        }
      } catch {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removeCurrentSpousePhoto = () => {
    setCurrentSpousePhotoFile(null);
    setCurrentSpousePhotoPreview(null);
    setCurrentSpouseFormData((prev) => ({ ...prev, profileImagePath: "" }));
  };

  const validateCurrentSpouseForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (
      editingCurrentSpouseId === null &&
      !currentSpousePhotoFile &&
      !currentSpousePhotoPreview
    ) {
      errors.photo = "Passport Size Photo is required";
    }

    if (!currentSpouseFormData.spouseName.trim()) {
      errors.spouseName = REQUIRED_FIELD_MESSAGE;
    }

    const primaryPhoneError = validatePhoneNumber(
      currentSpouseFormData.primaryPhone,
      { required: true },
    );
    if (primaryPhoneError) {
      errors.primaryPhone = primaryPhoneError;
    }

    const secondaryPhoneError = validatePhoneNumber(
      currentSpouseFormData.secondaryPhone,
    );
    if (secondaryPhoneError) {
      errors.secondaryPhone = secondaryPhoneError;
    }

    if (
      currentSpouseFormData.email.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentSpouseFormData.email.trim())
    ) {
      errors.email = "Enter a valid email address";
    }

    if (!currentSpouseFormData.address.trim()) {
      errors.address = REQUIRED_FIELD_MESSAGE;
    }

    setCurrentSpouseValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateCurrentSpouse = async () => {
    if (!application?.id) return;

    if (!validateCurrentSpouseForm()) {
      return;
    }

    const isEditing = editingCurrentSpouseId !== null;
    const confirmed = await showConfirm(
      isEditing ? "Update Current Spouse" : "Add Current Spouse",
      isEditing
        ? "Are you sure you want to update this current spouse record?"
        : "Are you sure you want to add this current spouse record?",
      isEditing ? "Yes, update" : "Yes, add",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setCurrentSpouseSubmitting(true);

      if (isEditing) {
        await currentSpouseService.updateCurrentSpouse(
          {
            id: editingCurrentSpouseId,
            ...currentSpouseFormData,
            certificateApplicationId: application.id,
          },
          currentSpousePhotoFile || undefined,
        );
      } else {
        await currentSpouseService.createCurrentSpouse(
          {
            ...currentSpouseFormData,
            certificateApplicationId: application.id,
          },
          currentSpousePhotoFile || undefined,
        );
      }

      showSuccessToast(
        isEditing
          ? "Current spouse updated successfully"
          : "Current spouse added successfully",
      );
      setCurrentSpouseFormData({
        certificateApplicationId: application.id,
        spouseName: "",
        primaryPhone: "",
        secondaryPhone: "",
        email: "",
        address: "",
        profileImagePath: "",
      });
      setCurrentSpouseValidationErrors({});
      setEditingCurrentSpouseId(null);
      setCurrentSpousePhotoFile(null);
      setCurrentSpousePhotoPreview(null);
      setShowCurrentSpouseForm(false);
      await fetchCurrentSpouses();
    } catch {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setCurrentSpouseSubmitting(false);
    }
  };

  const handleEditCurrentSpouse = (spouse: CurrentSpouse) => {
    setEditingCurrentSpouseId(spouse.id);
    setCurrentSpouseFormData({
      certificateApplicationId: spouse.certificateApplicationId,
      spouseName: spouse.spouseName || "",
      primaryPhone: spouse.primaryPhone || "",
      secondaryPhone: spouse.secondaryPhone || "",
      email: spouse.email || "",
      address: spouse.address || "",
      profileImagePath: spouse.profileImagePath || "",
    });
    const spousePhotoPath = spouse.photoUrl || spouse.profileImagePath;
    const spousePhotoUrl = getFullPath(spousePhotoPath);
    setCurrentSpousePhotoFile(null);
    setCurrentSpousePhotoPreview(spousePhotoUrl || null);
    setCurrentSpouseValidationErrors({});
    setShowCurrentSpouseForm(true);
  };

  const handleDeleteCurrentSpouse = async (spouse: CurrentSpouse) => {
    const confirmed = await showDeleteConfirm(
      spouse.spouseName,
      "current spouse",
    );

    if (!confirmed) return;

    try {
      setCurrentSpouseSubmitting(true);
      await currentSpouseService.deleteCurrentSpouse(spouse.id);
      showSuccessToast("Current spouse deleted successfully");

      if (editingCurrentSpouseId === spouse.id) {
        setEditingCurrentSpouseId(null);
        setShowCurrentSpouseForm(false);
        setCurrentSpouseValidationErrors({});
      }

      await fetchCurrentSpouses();
    } catch {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setCurrentSpouseSubmitting(false);
    }
  };

  const fetchApplicants = async () => {
    if (!id) return;

    try {
      setGroomLoading(true);
      const applicants = await applicantService.getApplicantsByCertificateId(
        Number(id),
      );

      // Find groom (applicantType === 0)
      const groom = applicants.find(
        (app: Applicant) => app.applicantType === ApplicantType.Groom,
      );

      if (groom) {
        setGroomApplicant(groom);
        setGroomDeclarationChecked(true);

        // Automatically set wasPreviouslyMarried based on existence of previous marriage data
        const hasPreviousMarriageData = !!(
          groom.previousMarriageDate || groom.previousSpouseName
        );

        setGroomFormData({
          userId: groom.userId,
          firstName: groom.firstName || "",
          middleName: groom.middleName || "",
          lastName: groom.lastName || "",
          genderId: groom.genderId || 1,
          dateOfBirth: groom.dateOfBirth ? groom.dateOfBirth.split("T")[0] : "",
          nationalIdNumber: groom.nationalIdNumber || "",
          email: groom.email || "",
          primaryPhone: groom.primaryPhone || "",
          secondaryPhone: groom.secondaryPhone || "",
          certificateApplicationId:
            groom.certificateApplicationId || Number(id),
          applicantType: "Groom",
          countyOfOriginId: groom.countyOfOriginId || 0,
          nationalityId: groom.nationalityId || 0,
          birthCountyId: groom.birthCountyId || 0,
          birthTown: groom.birthTown || "",
          currentAddress: groom.currentAddress || "",
          wasPreviouslyMarried: hasPreviousMarriageData,
          previousMarriageDate: groom.previousMarriageDate
            ? groom.previousMarriageDate.split("T")[0]
            : "",
          previousSpouseName: groom.previousSpouseName || "",
          fatherName: groom.fatherName || "",
          motherName: groom.motherName || "",
          profileImagePath: groom.profileImagePath || "",
        });
        // Set photo preview if exists
        const groomPhotoPath = groom.photoUrl || groom.profileImagePath;
        const groomPhotoUrl = getFullPath(groomPhotoPath);
        if (groomPhotoUrl) {
          setGroomPhotoPreview(groomPhotoUrl);
        }
        // Open accordion in edit mode when groom record exists
        setGroomAccordionOpen(true);
      }

      // Find bride (applicantType === 1)
      const bride = applicants.find(
        (app: Applicant) => app.applicantType === ApplicantType.Bride,
      );

      if (bride) {
        setBrideApplicant(bride);
        setBrideDeclarationChecked(true);

        // Automatically set wasPreviouslyMarried based on existence of previous marriage data
        const hasPreviousMarriageData = !!(
          bride.previousMarriageDate || bride.previousSpouseName
        );

        setBrideFormData({
          userId: bride.userId,
          firstName: bride.firstName || "",
          middleName: bride.middleName || "",
          lastName: bride.lastName || "",
          genderId: bride.genderId || 2,
          dateOfBirth: bride.dateOfBirth ? bride.dateOfBirth.split("T")[0] : "",
          nationalIdNumber: bride.nationalIdNumber || "",
          email: bride.email || "",
          primaryPhone: bride.primaryPhone || "",
          secondaryPhone: bride.secondaryPhone || "",
          certificateApplicationId:
            bride.certificateApplicationId || Number(id),
          applicantType: "Bride",
          countyOfOriginId: bride.countyOfOriginId || 0,
          nationalityId: bride.nationalityId || 0,
          birthCountyId: bride.birthCountyId || 0,
          birthTown: bride.birthTown || "",
          currentAddress: bride.currentAddress || "",
          wasPreviouslyMarried: hasPreviousMarriageData,
          previousMarriageDate: bride.previousMarriageDate
            ? bride.previousMarriageDate.split("T")[0]
            : "",
          previousSpouseName: bride.previousSpouseName || "",
          fatherName: bride.fatherName || "",
          motherName: bride.motherName || "",
          profileImagePath: bride.profileImagePath || "",
          brideProposedName: bride.brideProposedName || "",
        });
        // Set photo preview if exists
        const bridePhotoPath = bride.photoUrl || bride.profileImagePath;
        const bridePhotoUrl = getFullPath(bridePhotoPath);
        if (bridePhotoUrl) {
          setBridePhotoPreview(bridePhotoUrl);
        }
        // Open accordion in edit mode when bride record exists
        setBrideAccordionOpen(true);
      }
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setGroomLoading(false);
      setBrideLoading(false);
    }
  };

  const handleBrideFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setBrideFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setBrideFormData((prev) => ({ ...prev, [name]: Number(nextValue) }));
    } else {
      setBrideFormData((prev) => ({ ...prev, [name]: nextValue }));
    }

    // Clear validation error for this field when user starts typing
    if (brideValidationErrors[name]) {
      setBrideValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleGroomFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const nextValue =
      name === "primaryPhone" || name === "secondaryPhone"
        ? sanitizePhoneNumberInput(value)
        : value;

    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setGroomFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "number") {
      setGroomFormData((prev) => ({ ...prev, [name]: Number(nextValue) }));
    } else {
      setGroomFormData((prev) => ({ ...prev, [name]: nextValue }));
    }

    // Clear validation error for this field when user starts typing
    if (groomValidationErrors[name]) {
      setGroomValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleGroomPhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      try {
        setPhotoProcessing(true);
        const result = await processPassportPhoto(
          file,
          enableBackgroundRemoval,
        );
        setGroomPhotoFile(result.file);
        setGroomPhotoPreview(result.base64);
      } catch {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removeGroomPhoto = () => {
    setGroomPhotoFile(null);
    setGroomPhotoPreview(null);
    setGroomFormData({ ...groomFormData, profileImagePath: "" });
  };

  const handleBridePhotoChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showErrorToast(GENERIC_ERROR_MESSAGE);
        return;
      }
      try {
        setPhotoProcessing(true);
        const result = await processPassportPhoto(
          file,
          enableBackgroundRemoval,
        );
        setBridePhotoFile(result.file);
        setBridePhotoPreview(result.base64);
      } catch {
        showErrorToast(GENERIC_ERROR_MESSAGE);
      } finally {
        setPhotoProcessing(false);
      }
    }
  };

  const removeBridePhoto = () => {
    setBridePhotoFile(null);
    setBridePhotoPreview(null);
    setBrideFormData({ ...brideFormData, profileImagePath: "" });
  };

  const validateBrideForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!brideFormData.firstName?.trim()) {
      errors.firstName = "First Name is required";
    }
    if (!brideFormData.lastName?.trim()) {
      errors.lastName = "Last Name is required";
    }
    if (!brideFormData.dateOfBirth) {
      errors.dateOfBirth = "Date of Birth is required";
    }
    const bridePrimaryPhoneError = validatePhoneNumber(
      brideFormData.primaryPhone,
      {
        required: true,
      },
    );
    if (bridePrimaryPhoneError) {
      errors.primaryPhone = bridePrimaryPhoneError;
    }
    const brideSecondaryPhoneError = validatePhoneNumber(
      brideFormData.secondaryPhone,
    );
    if (brideSecondaryPhoneError) {
      errors.secondaryPhone = brideSecondaryPhoneError;
    }
    if (
      !brideFormData.countyOfOriginId ||
      brideFormData.countyOfOriginId === 0
    ) {
      errors.countyOfOriginId = "County of Origin is required";
    }
    if (!brideFormData.birthCountyId || brideFormData.birthCountyId === 0) {
      errors.birthCountyId = "Birth County is required";
    }
    if (!brideFormData.birthTown?.trim()) {
      errors.birthTown = "Birth Town is required";
    }
    if (!brideFormData.nationalityId || brideFormData.nationalityId === 0) {
      errors.nationalityId = "Nationality is required";
    }
    if (!brideFormData.currentAddress?.trim()) {
      errors.currentAddress = "Current Address is required";
    }
    if (!brideFormData.fatherName?.trim()) {
      errors.fatherName = "Father's Name is required";
    }
    if (!brideFormData.motherName?.trim()) {
      errors.motherName = "Mother's Name is required";
    }
    // Photo is required for new applicants
    if (!brideApplicant && !bridePhotoFile) {
      errors.photo = "Passport Size Photo is required";
    }

    setBrideValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateGroomForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!groomFormData.firstName?.trim()) {
      errors.firstName = "First Name is required";
    }
    if (!groomFormData.lastName?.trim()) {
      errors.lastName = "Last Name is required";
    }
    if (!groomFormData.dateOfBirth) {
      errors.dateOfBirth = "Date of Birth is required";
    }
    const groomPrimaryPhoneError = validatePhoneNumber(
      groomFormData.primaryPhone,
      {
        required: true,
      },
    );
    if (groomPrimaryPhoneError) {
      errors.primaryPhone = groomPrimaryPhoneError;
    }
    const groomSecondaryPhoneError = validatePhoneNumber(
      groomFormData.secondaryPhone,
    );
    if (groomSecondaryPhoneError) {
      errors.secondaryPhone = groomSecondaryPhoneError;
    }
    if (
      !groomFormData.countyOfOriginId ||
      groomFormData.countyOfOriginId === 0
    ) {
      errors.countyOfOriginId = "County of Origin is required";
    }
    if (!groomFormData.birthCountyId || groomFormData.birthCountyId === 0) {
      errors.birthCountyId = "Birth County is required";
    }
    if (!groomFormData.birthTown?.trim()) {
      errors.birthTown = "Birth Town is required";
    }
    if (!groomFormData.nationalityId || groomFormData.nationalityId === 0) {
      errors.nationalityId = "Nationality is required";
    }
    if (!groomFormData.currentAddress?.trim()) {
      errors.currentAddress = "Current Address is required";
    }
    if (!groomFormData.fatherName?.trim()) {
      errors.fatherName = "Father's Name is required";
    }
    if (!groomFormData.motherName?.trim()) {
      errors.motherName = "Mother's Name is required";
    }
    // Photo is required for new applicants
    if (!groomApplicant && !groomPhotoFile) {
      errors.photo = "Passport Size Photo is required";
    }

    setGroomValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBrideSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!application) return;

    // Validate form
    if (!validateBrideForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Bride Information",
      "Are you sure you want to submit the bride's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setBrideLoading(true);

      // Destructure wasPreviouslyMarried to exclude it from the payload
      const { wasPreviouslyMarried, profileImagePath, ...brideData } =
        brideFormData;

      // Clean up previous marriage fields - send undefined if empty or not previously married
      const cleanedData = {
        ...brideData,
        previousMarriageDate:
          brideFormData.wasPreviouslyMarried &&
          brideFormData.previousMarriageDate
            ? brideFormData.previousMarriageDate
            : undefined,
        previousSpouseName:
          brideFormData.wasPreviouslyMarried && brideFormData.previousSpouseName
            ? brideFormData.previousSpouseName
            : undefined,
      };

      if (brideApplicant) {
        // UPDATE existing bride
        const payload = {
          ...cleanedData,
          id: brideApplicant.id,
          userId: brideApplicant.userId,
          certificateApplicationId: application.id,
          applicantType: "Bride",
          roleId: BasicRole,
        };
        await applicantService.updateApplicant(
          brideApplicant.id!,
          payload as UpdateApplicantDto,
          bridePhotoFile || undefined,
        );
        showSuccessToast("Bride details updated successfully");
      } else {
        // CREATE new bride
        const payload = {
          ...cleanedData,
          certificateApplicationId: application.id,
          applicantType: "Bride",
          roleId: BasicRole,
        };
        await applicantService.createApplicant(
          payload as CreateApplicantDto,
          bridePhotoFile || undefined,
        );
        showSuccessToast("Bride details saved successfully");
      }

      // Refresh applicant data
      await fetchApplicants();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setBrideLoading(false);
    }
  };

  const handleGroomSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!application) return;

    // Validate form
    if (!validateGroomForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Groom Information",
      "Are you sure you want to submit the groom's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setGroomLoading(true);

      // Destructure wasPreviouslyMarried to exclude it from the payload
      const { wasPreviouslyMarried, profileImagePath, ...groomData } =
        groomFormData;

      // Clean up previous marriage fields - send undefined if empty or not previously married
      const cleanedData = {
        ...groomData,
        previousMarriageDate:
          groomFormData.wasPreviouslyMarried &&
          groomFormData.previousMarriageDate
            ? groomFormData.previousMarriageDate
            : undefined,
        previousSpouseName:
          groomFormData.wasPreviouslyMarried && groomFormData.previousSpouseName
            ? groomFormData.previousSpouseName
            : undefined,
      };

      if (groomApplicant) {
        // UPDATE existing groom
        const payload = {
          ...cleanedData,
          id: groomApplicant.id,
          userId: groomApplicant.userId,
          certificateApplicationId: application.id,
          applicantType: "Groom",
          roleId: BasicRole,
        };
        await applicantService.updateApplicant(
          groomApplicant.id!,
          payload as UpdateApplicantDto,
          groomPhotoFile || undefined,
        );
        showSuccessToast("Groom details updated successfully");
      } else {
        // CREATE new groom
        const payload = {
          ...cleanedData,
          certificateApplicationId: application.id,
          applicantType: "Groom",
          roleId: BasicRole,
        };
        await applicantService.createApplicant(
          payload as CreateApplicantDto,
          groomPhotoFile || undefined,
        );
        showSuccessToast("Groom details saved successfully");
      }

      // Refresh applicant data
      await fetchApplicants();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setGroomLoading(false);
    }
  };

  const handleDeleteGroom = async () => {
    if (!groomApplicant) return;

    const confirmed = await showDeleteConfirm(
      groomApplicant.firstName + " " + groomApplicant.lastName,
      "groom applicant",
    );

    if (!confirmed) return;

    try {
      setGroomLoading(true);
      await applicantService.deleteApplicant(groomApplicant.id!);
      showSuccessToast("Groom details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setGroomApplicant(null);
      // Reset photo state
      setGroomPhotoFile(null);
      setGroomPhotoPreview(null);
      // Reset form to initial state
      setGroomFormData({
        userId: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        genderId: 1,
        dateOfBirth: "",
        nationalIdNumber: "",
        email: "",
        primaryPhone: "",
        secondaryPhone: "",
        certificateApplicationId: application?.id || 0,
        applicantType: "Groom",
        countyOfOriginId: 0,
        nationalityId: 0,
        birthCountyId: 0,
        birthTown: "",
        currentAddress: "",
        wasPreviouslyMarried: false,
        previousMarriageDate: "",
        previousSpouseName: "",
        fatherName: "",
        motherName: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setGroomLoading(false);
    }
  };

  const handleDeleteBride = async () => {
    if (!brideApplicant) return;

    const confirmed = await showDeleteConfirm(
      brideApplicant.firstName + " " + brideApplicant.lastName,
      "bride applicant",
    );

    if (!confirmed) return;

    try {
      setBrideLoading(true);
      await applicantService.deleteApplicant(brideApplicant.id!);
      showSuccessToast("Bride details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setBrideApplicant(null);
      // Reset photo state
      setBridePhotoFile(null);
      setBridePhotoPreview(null);
      // Reset form to initial state
      setBrideFormData({
        userId: undefined,
        firstName: "",
        middleName: "",
        lastName: "",
        genderId: 2,
        dateOfBirth: "",
        nationalIdNumber: "",
        email: "",
        primaryPhone: "",
        secondaryPhone: "",
        certificateApplicationId: application?.id || 0,
        applicantType: "Bride",
        countyOfOriginId: 0,
        nationalityId: 0,
        birthCountyId: 0,
        birthTown: "",
        currentAddress: "",
        wasPreviouslyMarried: false,
        previousMarriageDate: "",
        previousSpouseName: "",
        fatherName: "",
        motherName: "",
        brideProposedName: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setBrideLoading(false);
    }
  };

  const handleOfficiatorFormChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    const nextValue =
      name === "officiatorContact" || name === "witnessContact"
        ? sanitizePhoneNumberInput(value)
        : value;
    setOfficiatorFormData((prev) => ({ ...prev, [name]: nextValue }));

    // Clear validation error for this field when user starts typing
    if (officiatorValidationErrors[name]) {
      setOfficiatorValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateOfficiatorForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields validation
    if (!officiatorFormData.officiatorName?.trim()) {
      errors.officiatorName = "Officiator Name is required";
    }
    const officiatorContactError = validatePhoneNumber(
      officiatorFormData.officiatorContact,
      { required: true },
    );
    if (officiatorContactError) {
      errors.officiatorContact = officiatorContactError;
    }
    if (!officiatorFormData.witnessName?.trim()) {
      errors.witnessName = "Witness Name is required";
    }
    const witnessContactError = validatePhoneNumber(
      officiatorFormData.witnessContact,
      { required: true },
    );
    if (witnessContactError) {
      errors.witnessContact = witnessContactError;
    }

    setOfficiatorValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOfficiatorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!application) return;

    // Validate form
    if (!validateOfficiatorForm()) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Officiator Information",
      "Are you sure you want to submit the officiator's information?",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setOfficiatorLoading(true);

      if (officiator) {
        // UPDATE existing officiator
        const payload = {
          ...officiatorFormData,
          id: officiator.id,
          certificateApplicationId: application.id,
        };
        await officiatorService.updateOfficiator(
          officiator.id!,
          payload as UpdateOfficiatorDto,
        );
        showSuccessToast("Officiator details updated successfully");
      } else {
        // CREATE new officiator
        const payload = {
          ...officiatorFormData,
          certificateApplicationId: application.id!,
        };
        await officiatorService.createOfficiator(payload);
        showSuccessToast("Officiator details saved successfully");
      }

      // Refresh officiator data
      await fetchOfficiator();
      // Refresh submission requirements
      await checkReadyForSubmission(application.id!);
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setOfficiatorLoading(false);
    }
  };

  const handleDeleteOfficiator = async () => {
    if (!officiator) return;

    const confirmed = await showDeleteConfirm(
      officiator.officiatorName,
      "officiator details",
    );

    if (!confirmed) return;

    try {
      setOfficiatorLoading(true);
      await officiatorService.deleteOfficiator(officiator.id!);
      showSuccessToast("Officiator details deleted successfully");
      // Refresh submission requirements
      await checkReadyForSubmission(application?.id!);
      setOfficiator(null);
      // Reset form to initial state
      setOfficiatorFormData({
        certificateApplicationId: application?.id || 0,
        officiatorName: "",
        officiatorContact: "",
        witnessName: "",
        witnessContact: "",
      });
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setOfficiatorLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!application) return;

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Submit Application",
      "Are you sure you want to submit this application? This action cannot be undone.",
      "Yes, submit",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Submitted,
      );
      showSuccessToast("Application submitted successfully");
      // Send email notification if settings allow
      if (application.workflowId) {
        notificationService
          .sendEmailNotification(application.workflowId, application.id!)
          .catch(() => {});
      }
      // Refresh the application data to show updated status
      await fetchData();
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!application || !application.workflowId) return;
    const confirmed = await showConfirm(
      "Approve Application",
      `Are you sure you want to approve this marriage certificate application?`,
      "Yes, approve",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);

      // Get workflow details to check if this is the final stage
      const workflow = await workflowService.getWorkflowById(
        application.workflowId,
      );
      const totalStages = workflow.stages?.length || 0;
      const isFinalStage = currentStageOrder >= totalStages;

      // Approve the workflow action
      await workflowService.approve({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: "Application approved",
      });

      // If this is the final approval stage, update status to Approved
      if (isFinalStage) {
        await certificateService.changeStatus(
          application.id!,
          UserStatusId.Approved,
        );
      }

      await fetchData();
      showSuccessToast("Application has been approved successfully.");
      // Send email notification if settings allow
      notificationService
        .sendEmailNotification(application.workflowId, application.id!)
        .catch(() => {});
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuery = () => {
    if (!application || !application.workflowId) return;
    setShowQueryForm(true);
  };

  const handleSubmitQuery = async (payload: QueryWorkflowActionDto) => {
    if (!application || !application.workflowId) return;
    try {
      setActionLoading(true);

      await workflowService.query(payload);

      // Change status to Queried to move the workflow back one step
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Queried,
      );

      await fetchData();
      showSuccessToast("Application has been queried successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!application || !application.workflowId) return;

    // Import Swal for custom input dialog
    const Swal = (await import("sweetalert2")).default;

    const result = await Swal.fire({
      title: "Reject Application",
      html: `
        <p>Please provide a reason for rejecting this application:</p>
      `,
      input: "textarea",
      inputPlaceholder: "Enter your rejection reason here...",
      inputAttributes: {
        "aria-label": "Rejection reason",
        style: "min-height: 100px;",
      },
      showCancelButton: true,
      confirmButtonText: "Reject Application",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "You must provide a reason for rejection!";
        }
      },
    });

    if (!result.isConfirmed || !result.value || !result.value.trim()) return;

    const rejectComment = result.value.trim();

    try {
      setActionLoading(true);

      // Reject the workflow action with the provided comment
      await workflowService.reject({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: rejectComment,
      });

      // Change status to Rejected
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Rejected,
      );

      await fetchData();
      showSuccessToast("Application has been rejected successfully.");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const openEditModal = () => {
    if (!application) return;

    const enforcedCountyId =
      isRoleCountyLinked && linkedCountyId
        ? linkedCountyId
        : application.countyId;

    setEditFormData({
      category: application.category,
      email: application.email || "",
      placeOfWedding: application.placeOfWedding || "",
      dateOfWedding: application.dateOfWedding?.split("T")[0] || "",
      countyId: enforcedCountyId,
      workflowId: application.workflowId,
      dateOfCertification: application.dateOfCertification?.split("T")[0],
      processAtHeadquarters: isRoleCountyLinked
        ? false
        : application.processAtHeadquarters || false,
    });
    setShowEditModal(true);
  };

  const resetEditForm = () => {
    setEditFormData({});
    setEditError("");
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");
    if (!application || !application.id) {
      setEditError("Application ID is missing");
      return;
    }

    try {
      setEditLoading(true);

      // Include the id and formNo in the payload as required by the API
      const updatePayload: UpdateCertificateApplicationDto = {
        id: application.id,
        category: editFormData.category,
        email: editFormData.email,
        placeOfWedding: editFormData.placeOfWedding,
        countyId: editFormData.countyId,
        workflowId: editFormData.workflowId,
        processAtHeadquarters: editFormData.processAtHeadquarters,
        dateOfWedding: editFormData.dateOfWedding,
        dateOfCertification: editFormData.dateOfCertification,
        lraPayment: editServiceCharge?.lraPayment ?? application.lraPayment,
        operationalCost:
          editServiceCharge?.operationalCost ?? application.operationalCost,
        currencyId: editServiceCharge?.currencyId ?? application.currencyId,
        statusId: application.statusId,
      };

      // Convert date strings to ISO format if they exist
      if (updatePayload.dateOfWedding) {
        // If it's just a date (YYYY-MM-DD), convert to ISO datetime
        if (updatePayload.dateOfWedding.length === 10) {
          updatePayload.dateOfWedding = new Date(
            updatePayload.dateOfWedding,
          ).toISOString();
        }
      }

      if (updatePayload.dateOfCertification) {
        // If it's just a date (YYYY-MM-DD), convert to ISO datetime
        if (updatePayload.dateOfCertification.length === 10) {
          updatePayload.dateOfCertification = new Date(
            updatePayload.dateOfCertification,
          ).toISOString();
        }
      }

      console.log("Update Payload:", updatePayload);
      console.log("Application ID in URL:", application.id);

      await certificateService.updateCertificateApplication(
        application.id,
        updatePayload,
      );
      setShowEditModal(false);
      resetEditForm();
      await fetchData();
      showSuccessToast("Application updated successfully.");
    } catch (err: any) {
      console.error("Update error:", err);
      setEditError(err?.message || "Failed to update application");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!application) return;
    const confirmed = await showDeleteConfirm(
      application.formNo,
      "marriage certificate application",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);
      await certificateService.deleteCertificateApplication(application.id!);
      showSuccessToast("Application has been deleted successfully.");
      navigate(getBackPath());
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
      setActionLoading(false);
    }
  };

  const handlePayment = (method: string) => {
    setPaymentDropdownOpen(false);

    if (!hasRequiredPaymentDetails) {
      showWarningToast(
        "Add Groom, Bride and Officiator details before making payment.",
      );
      return;
    }

    if (!hasAttachmentsAddedRequirement) {
      showWarningToast("Upload required attachments before making payment.");
      return;
    }

    // Show payment modal or navigate
    switch (method) {
      case "Manual":
        openManualPaymentModal();
        break;
      case "Card":
        openCardPaymentModal();
        break;
      case "Mobile Money":
        window.open("https://paymentcollectclient.triaxo.com/", "_blank");
        break;
      default:
        showSuccessToast(`Redirecting to ${method} payment...`);
    }
  };

  const openCardPaymentModal = () => {
    if (!application?.id) return;
    // CardPayment modal uses Stripe and doesn't need lookup data
    setShowCardPaymentModal(true);
  };

  const openManualPaymentModal = () => {
    if (!application?.id) return;
    setShowManualPaymentModal(true);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Self-service: all readiness items except payment must be satisfied before the
  // applicant can continue to the summary (payment is made on the summary page).
  const nonPaymentRequirements = Object.entries(submissionRequirements).filter(
    ([key]) => !key.toLowerCase().includes("payment"),
  );
  const canContinueToSummary =
    nonPaymentRequirements.length > 0 &&
    nonPaymentRequirements.every(([, value]) => value === true);
  // In portal mode the checklist excludes payment, so "ready" reflects the records only.
  const submitSectionReady = isPortal
    ? canContinueToSummary
    : isReadyForSubmission;

  // A submitted self-service application is read-only — hide all save/update buttons.
  const portalReadOnly =
    isPortal &&
    !!application &&
    application.statusId !== UserStatusId.Created &&
    application.statusId !== UserStatusId.Queried;

  if (!HasPermission(ModuleId.Weddings, ApplicationPermissionId.Apply)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to view marriage certificate
              applications
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={
        isPortal
          ? `portal-detail-body${portalReadOnly ? " portal-readonly" : ""}`
          : `dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`
      }
    >
      {!isPrintPage && !isPortal && (
        <>
          <Topbar
            onMenuToggle={() => setSidebarOpen((prev) => !prev)}
            sidebarOpen={sidebarOpen}
          />
          <Sidebar isOpen={sidebarOpen} />
        </>
      )}
      {isPortal && (
        <header className="portal-detail-topbar">
          <button
            type="button"
            className="portal-btn portal-btn--ghost"
            onClick={() => navigate("/portal")}
          >
            ← My Applications
          </button>
          <span className="portal-detail-title">Marriage Application</span>
          <button
            type="button"
            className="portal-btn portal-btn--primary"
            onClick={() => navigate(`/portal/summary/${id}`)}
            disabled={!id || !canContinueToSummary}
            title={
              canContinueToSummary
                ? "Continue to summary & payment"
                : "Complete all required items first"
            }
            style={
              !canContinueToSummary
                ? { opacity: 0.5, cursor: "not-allowed" }
                : undefined
            }
          >
            Continue to Summary →
          </button>
        </header>
      )}
      <main className="main-content">
        <div className="user-detail-page">
          <BusyOverlay
            isVisible={loading || actionLoading || photoProcessing}
            message={
              photoProcessing
                ? "Processing photo — removing background & resizing..."
                : actionLoading
                  ? "Processing..."
                  : "Loading application details..."
            }
          />

          {/* Print View - Marriage Certificate */}
          {isPrintPage && application && groomApplicant && brideApplicant && (
            <>
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  background: "#fff",
                }}
                className="no-print"
              >
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: "12px 32px",
                    fontSize: "16px",
                    background: "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  🖨️ Print Certificate
                </button>
                <button
                  onClick={() => navigate(getBackPath())}
                  style={{
                    padding: "12px 32px",
                    fontSize: "16px",
                    background: "#6b7280",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontWeight: "600",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    marginLeft: "10px",
                  }}
                >
                  ← Back
                </button>
              </div>
              <CountyMarriageCert
                groomName={`${groomApplicant.firstName} ${groomApplicant.middleName || ""} ${groomApplicant.lastName}`.trim()}
                groomDateOfBirth={groomApplicant.dateOfBirth || ""}
                groomPlaceOfBirth={groomApplicant.birthTown || ""}
                groomCountyOfBirth={
                  counties.find((c) => c.id === groomApplicant.birthCountyId)
                    ?.name || ""
                }
                groomFatherName={groomApplicant.fatherName || ""}
                groomMotherName={groomApplicant.motherName || ""}
                brideName={`${brideApplicant.firstName} ${brideApplicant.middleName || ""} ${brideApplicant.lastName}`.trim()}
                brideProposedName={brideApplicant.brideProposedName || ""}
                brideDateOfBirth={brideApplicant.dateOfBirth || ""}
                bridePlaceOfBirth={brideApplicant.birthTown || ""}
                brideCountyOfBirth={
                  counties.find((c) => c.id === brideApplicant.birthCountyId)
                    ?.name || ""
                }
                brideFatherName={brideApplicant.fatherName || ""}
                brideMotherName={brideApplicant.motherName || ""}
                placeOfWedding={application.placeOfWedding || ""}
                dateOfWedding={application.dateOfWedding || ""}
                countyName={application.countyName || ""}
                certificateCode={application.formNo || ""}
                revenueNumber=""
                ministryName={ministryName}
                groomPhotoUrl={
                  groomApplicant.photoUrl || groomApplicant.profileImagePath
                }
                bridePhotoUrl={
                  brideApplicant.photoUrl || brideApplicant.profileImagePath
                }
                certificateApplicationId={application.id}
              />
            </>
          )}

          {application && !isPrintPage && (
            <div className="user-detail-layout">
              <button
                className="user-detail-back user-detail-back--mobile"
                onClick={() => navigate(getBackPath())}
              >
                ← Back to Applications
              </button>

              {/* Column 1: Application Details */}
              <div className="user-detail-info">
                {/* Profile header */}
                <div className="user-detail-profile-header">
                  <div className="user-detail-avatar">📄</div>
                  <div className="user-detail-profile-info">
                    <h2>{application.formNo}</h2>
                    <div className="user-detail-profile-meta">
                      <span>{application.category}</span>
                      <span className="divider">-</span>
                      <span className={`status-badge status-badge--active`}>
                        {application.status?.name ||
                          application.statusName ||
                          "Pending"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Marriage Application Information */}
                <h3 className="user-detail-section-title">
                  Marriage Application Information
                </h3>
                <div
                  className="user-detail-grid"
                  style={{
                    gridTemplateColumns: "repeat(3, 1fr)",
                  }}
                >
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Category</div>
                    <div className="user-detail-field-value">
                      {application.category}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">County</div>
                    <div
                      className={`user-detail-field-value ${!application.countyName ? "empty" : ""}`}
                    >
                      {application.countyName || "-"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Place of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.placeOfWedding ? "empty" : ""}`}
                    >
                      {application.placeOfWedding || "-"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfWedding ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfWedding) || "-"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Certification
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfCertification ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfCertification) || "-"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Email</div>
                    <div
                      className={`user-detail-field-value ${!application.email ? "empty" : ""}`}
                    >
                      {application.email || "-"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Created Date</div>
                    <div
                      className={`user-detail-field-value ${!application.createdAt ? "empty" : ""}`}
                    >
                      {formatDate(application.createdAt) || "-"}
                    </div>
                  </div>
                </div>

                {/* Signed Certificate Display (Finalize Page Only) */}
                {isFinalizePage && signedCertificate && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      style={{
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "1rem",
                          marginBottom: "0.5rem",
                          color: "#28a745",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>📄 Signed Certificate</span>
                        <button
                          onClick={handleDeleteSignedCertificate}
                          disabled={signedCertLoading}
                          style={{
                            background: "#dc3545",
                            color: "#fff",
                            border: "none",
                            padding: "0.5rem 1rem",
                            borderRadius: "4px",
                            cursor: signedCertLoading
                              ? "not-allowed"
                              : "pointer",
                            fontSize: "0.875rem",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            opacity: signedCertLoading ? 0.6 : 1,
                          }}
                          title="Delete signed certificate"
                        >
                          {signedCertLoading ? (
                            <>
                              <span className="spinner spinner--sm"></span>
                              Deleting...
                            </>
                          ) : (
                            <>
                              <span>🗑️</span> Delete
                            </>
                          )}
                        </button>
                      </div>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "#6b7280",
                          marginBottom: "1rem",
                        }}
                      >
                        Uploaded on:{" "}
                        {new Date(signedCertificate.createdAt).toLocaleString()}
                      </div>
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderRadius: "4px",
                          overflow: "hidden",
                          background: "#fff",
                        }}
                      >
                        {signedCertificate.documentPath ? (
                          (() => {
                            const fullUrl = getFullPath(
                              signedCertificate.documentPath,
                            );
                            if (!fullUrl) {
                              return (
                                <div
                                  style={{
                                    padding: "2rem",
                                    textAlign: "center",
                                    color: "#6b7280",
                                  }}
                                >
                                  Unable to display certificate
                                </div>
                              );
                            }
                            return signedCertificate.documentPath
                              .toLowerCase()
                              .endsWith(".pdf") ? (
                              <iframe
                                src={fullUrl || undefined}
                                title="Signed Certificate"
                                style={{
                                  width: "100%",
                                  height: "600px",
                                  border: "none",
                                }}
                              />
                            ) : (
                              <img
                                src={fullUrl || undefined}
                                alt="Signed Certificate"
                                style={{
                                  width: "100%",
                                  maxHeight: "800px",
                                  objectFit: "contain",
                                }}
                              />
                            );
                          })()
                        ) : (
                          <div
                            style={{
                              padding: "2rem",
                              textAlign: "center",
                              color: "#6b7280",
                            }}
                          >
                            Unable to display certificate
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Groom's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      onClick={() => setGroomAccordionOpen(!groomAccordionOpen)}
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Groom's Detail</span>
                      <span>{groomAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {groomAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleGroomSubmit}>
                          {/* Photo Upload */}
                          <div
                            style={{
                              marginBottom: "1.5rem",
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "500",
                                fontSize: "0.875rem",
                                color: "#374151",
                              }}
                            >
                              Passport Size Photo{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="photo-upload-container">
                              {groomPhotoPreview ? (
                                <div className="photo-preview photo-preview--spread">
                                  <img
                                    src={groomPhotoPreview || undefined}
                                    alt="Groom Preview"
                                    className="preview-image"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      img.style.border = "2px solid red";
                                      img.alt = "Failed to load image";
                                    }}
                                  />
                                  {!isFinalizePage && (
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--danger"
                                      onClick={removeGroomPhoto}
                                    >
                                      Remove Photo
                                    </button>
                                  )}
                                </div>
                              ) : (
                                !isFinalizePage && (
                                  <div className="photo-upload-area">
                                    <input
                                      type="file"
                                      id="groomPhotoUpload"
                                      accept="image/*"
                                      onChange={handleGroomPhotoChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="groomPhotoUpload"
                                      className="photo-upload-label"
                                    >
                                      <div className="upload-icon">📷</div>
                                      <div className="upload-text">
                                        <span className="upload-title">
                                          Click to upload photo
                                        </span>
                                        <span className="upload-subtitle">
                                          PNG, JPG up to 5MB
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                            {groomValidationErrors.photo && (
                              <div className="field-error">
                                {groomValidationErrors.photo}
                              </div>
                            )}
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${groomFormData.firstName ? "has-value" : ""} ${groomValidationErrors.firstName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">First Name *</label>
                              <input
                                type="text"
                                className={`form-input ${groomValidationErrors.firstName ? "form-input--error" : ""}`}
                                name="firstName"
                                value={groomFormData.firstName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.firstName && (
                                <div className="field-error">
                                  {groomValidationErrors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.middleName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Middle Name</label>
                              <input
                                type="text"
                                className="form-input"
                                name="middleName"
                                value={groomFormData.middleName}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${groomFormData.lastName ? "has-value" : ""} ${groomValidationErrors.lastName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">Last Name *</label>
                              <input
                                type="text"
                                className={`form-input ${groomValidationErrors.lastName ? "form-input--error" : ""}`}
                                name="lastName"
                                value={groomFormData.lastName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.lastName && (
                                <div className="field-error">
                                  {groomValidationErrors.lastName}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">Gender *</label>
                              <select
                                className="form-input"
                                name="genderId"
                                value={groomFormData.genderId}
                                onChange={handleGroomFormChange}
                                required
                                disabled
                                style={{
                                  background: "#f1f1f1",
                                  cursor: "not-allowed",
                                }}
                              >
                                <option value={1}>Male</option>
                                <option value={2}>Female</option>
                              </select>
                            </div>
                            <div
                              className={`form-group has-value ${groomValidationErrors.dateOfBirth ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                className={`form-input ${groomValidationErrors.dateOfBirth ? "form-input--error" : ""}`}
                                name="dateOfBirth"
                                value={groomFormData.dateOfBirth}
                                onChange={handleGroomFormChange}
                                max={getMaxDateForAge(groomMinAge)}
                                required
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.dateOfBirth && (
                                <div className="field-error">
                                  {groomValidationErrors.dateOfBirth}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.nationalIdNumber ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                National ID Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="nationalIdNumber"
                                value={groomFormData.nationalIdNumber}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${groomFormData.primaryPhone ? "has-value" : ""} ${groomValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="tel"
                                className={`form-input ${groomValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                                name="primaryPhone"
                                value={groomFormData.primaryPhone}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.primaryPhone && (
                                <div className="field-error">
                                  {groomValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.secondaryPhone ? "has-value" : ""} ${groomValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="tel"
                                className={`form-input ${groomValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                                name="secondaryPhone"
                                value={groomFormData.secondaryPhone}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.secondaryPhone && (
                                <div className="field-error">
                                  {groomValidationErrors.secondaryPhone}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Origin & Birth Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group has-value ${groomValidationErrors.countyOfOriginId ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                County of Origin *
                              </label>
                              <select
                                className={`form-input ${groomValidationErrors.countyOfOriginId ? "form-input--error" : ""}`}
                                name="countyOfOriginId"
                                value={groomFormData.countyOfOriginId}
                                onChange={handleGroomFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {groomValidationErrors.countyOfOriginId && (
                                <div className="field-error">
                                  {groomValidationErrors.countyOfOriginId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group has-value ${groomValidationErrors.birthCountyId ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Birth County *
                              </label>
                              <select
                                className={`form-input ${groomValidationErrors.birthCountyId ? "form-input--error" : ""}`}
                                name="birthCountyId"
                                value={groomFormData.birthCountyId}
                                onChange={handleGroomFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {groomValidationErrors.birthCountyId && (
                                <div className="field-error">
                                  {groomValidationErrors.birthCountyId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.birthTown ? "has-value" : ""} ${groomValidationErrors.birthTown ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">Birth Town *</label>
                              <input
                                type="text"
                                className={`form-input ${groomValidationErrors.birthTown ? "form-input--error" : ""}`}
                                name="birthTown"
                                value={groomFormData.birthTown}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.birthTown && (
                                <div className="field-error">
                                  {groomValidationErrors.birthTown}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group has-value ${groomValidationErrors.nationalityId ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Nationality *
                              </label>
                              <select
                                className={`form-input ${groomValidationErrors.nationalityId ? "form-input--error" : ""}`}
                                name="nationalityId"
                                value={groomFormData.nationalityId}
                                onChange={handleGroomFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select Nationality</option>
                                {nationalities.map((nationality) => (
                                  <option
                                    key={nationality.id}
                                    value={nationality.id}
                                  >
                                    {nationality.name}
                                  </option>
                                ))}
                              </select>
                              {groomValidationErrors.nationalityId && (
                                <div className="field-error">
                                  {groomValidationErrors.nationalityId}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${groomFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={groomFormData.email}
                                onChange={handleGroomFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>

                            <div
                              className={`form-group ${groomFormData.currentAddress ? "has-value" : ""} ${groomValidationErrors.currentAddress ? "form-group--error" : ""}`}
                              style={{ gridColumn: "span 3" }}
                            >
                              <label className="form-label">
                                Current Address *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${groomValidationErrors.currentAddress ? "form-input--error" : ""}`}
                                name="currentAddress"
                                value={groomFormData.currentAddress}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.currentAddress && (
                                <div className="field-error">
                                  {groomValidationErrors.currentAddress}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Family Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${groomFormData.fatherName ? "has-value" : ""} ${groomValidationErrors.fatherName ? "form-group--error" : ""}`}
                              style={{ gridColumn: "span 2" }}
                            >
                              <label className="form-label">
                                Father's Name *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${groomValidationErrors.fatherName ? "form-input--error" : ""}`}
                                name="fatherName"
                                value={groomFormData.fatherName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.fatherName && (
                                <div className="field-error">
                                  {groomValidationErrors.fatherName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${groomFormData.motherName ? "has-value" : ""} ${groomValidationErrors.motherName ? "form-group--error" : ""}`}
                              style={{ gridColumn: "span 2" }}
                            >
                              <label className="form-label">
                                Mother's Name *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${groomValidationErrors.motherName ? "form-input--error" : ""}`}
                                name="motherName"
                                value={groomFormData.motherName}
                                onChange={handleGroomFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {groomValidationErrors.motherName && (
                                <div className="field-error">
                                  {groomValidationErrors.motherName}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <>
                              {declarationText && (
                                <div style={{ marginBottom: "0.85rem" }}>
                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: "0.6rem",
                                      cursor: "pointer",
                                      color: "#495057",
                                      fontSize: "0.9rem",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={groomDeclarationChecked}
                                      onChange={(e) =>
                                        setGroomDeclarationChecked(
                                          e.target.checked,
                                        )
                                      }
                                      style={{ marginTop: "0.15rem" }}
                                    />
                                    <span>{declarationText}</span>
                                  </label>
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  gap: "0.75rem",
                                  justifyContent: "flex-end",
                                  marginBottom: "1.5rem",
                                }}
                              >
                                {groomApplicant && (
                                  <button
                                    type="button"
                                    onClick={handleDeleteGroom}
                                    disabled={groomLoading}
                                    style={{
                                      padding: "0.65rem 1.5rem",
                                      background: groomLoading
                                        ? "#ccc"
                                        : "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor: groomLoading
                                        ? "not-allowed"
                                        : "pointer",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Delete Groom
                                  </button>
                                )}
                                <button
                                  type="submit"
                                  disabled={
                                    groomLoading ||
                                    (declarationText
                                      ? !groomDeclarationChecked
                                      : false)
                                  }
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: groomLoading
                                      ? "#ccc"
                                      : declarationText &&
                                          !groomDeclarationChecked
                                        ? "#ccc"
                                        : "#007bff",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: groomLoading
                                      ? "not-allowed"
                                      : declarationText &&
                                          !groomDeclarationChecked
                                        ? "not-allowed"
                                        : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  {groomLoading
                                    ? "Saving..."
                                    : groomApplicant
                                      ? "Update Groom"
                                      : "Save Groom"}
                                </button>
                              </div>
                            </>
                          )}

                          {/* Ex-Spouse or Spouses Accordion */}
                          <div style={{ marginBottom: "1.5rem" }}>
                            <div
                              onClick={() => {
                                if (!canManageGroomSpouses) return;
                                setExSpousesAccordionOpen(
                                  !exSpousesAccordionOpen,
                                );
                              }}
                              style={{
                                cursor: canManageGroomSpouses
                                  ? "pointer"
                                  : "not-allowed",
                                padding: "0.85rem 1rem",
                                background: canManageGroomSpouses
                                  ? "#e8f0fe"
                                  : "#f1f3f5",
                                border: canManageGroomSpouses
                                  ? "1px solid #c5d5f5"
                                  : "1px solid #dee2e6",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontWeight: "600",
                                fontSize: "0.95rem",
                                color: canManageGroomSpouses
                                  ? "inherit"
                                  : "#6c757d",
                              }}
                            >
                              <span>Ex-Spouse(s)</span>
                              <span>{exSpousesAccordionOpen ? "▼" : "▶"}</span>
                            </div>
                            {!canManageGroomSpouses && (
                              <div
                                style={{
                                  marginTop: "0.5rem",
                                  color: "#6c757d",
                                  fontSize: "0.85rem",
                                }}
                              >
                                Save groom details first to manage ex-spouse
                                records.
                              </div>
                            )}
                            {canManageGroomSpouses &&
                              exSpousesAccordionOpen && (
                                <div
                                  style={{
                                    border: "1px solid #dee2e6",
                                    borderTop: "none",
                                    padding: "1rem",
                                    borderRadius: "0 0 4px 4px",
                                  }}
                                >
                                  {!isFinalizePage && (
                                    <div style={{ marginBottom: "1rem" }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingExSpouseId(null);
                                            setExSpouseFormData({
                                              certificateApplicationId:
                                                application?.id || Number(id),
                                              isGroomEx: true,
                                              spouseName: "",
                                              primaryPhone: "",
                                              secondaryPhone: "",
                                              email: "",
                                              address: "",
                                              profileImagePath: "",
                                            });
                                            setExSpouseValidationErrors({});
                                            setExSpousePhotoFile(null);
                                            setExSpousePhotoPreview(null);
                                            setShowExSpouseForm(true);
                                          }}
                                          style={{
                                            padding: "0.55rem 1rem",
                                            background: "#007bff",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Add Ex-Spouse
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {exSpouseLoading ? (
                                    <div style={{ color: "#6c757d" }}>
                                      Loading ex-spouses...
                                    </div>
                                  ) : exSpouses.length > 0 ? (
                                    <div style={{ overflowX: "auto" }}>
                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          background: "#fff",
                                        }}
                                      >
                                        <thead>
                                          <tr style={{ background: "#f8f9fa" }}>
                                            <th
                                              style={{
                                                textAlign: "center",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                                width: "70px",
                                              }}
                                            >
                                              Photo
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Spouse Name
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Primary Phone
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Secondary Phone
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Email
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Address
                                            </th>
                                            {!isFinalizePage && (
                                              <th
                                                style={{
                                                  textAlign: "center",
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                  width: "90px",
                                                }}
                                              >
                                                Actions
                                              </th>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {exSpouses.map((spouse) => (
                                            <tr key={spouse.id}>
                                              <td
                                                style={{
                                                  padding: "0.4rem",
                                                  border: "1px solid #dee2e6",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {spouse.photoUrl ? (
                                                  <img
                                                    src={
                                                      getFullPath(
                                                        spouse.photoUrl,
                                                      ) || undefined
                                                    }
                                                    alt={
                                                      spouse.spouseName ||
                                                      "Ex-Spouse"
                                                    }
                                                    style={{
                                                      width: "48px",
                                                      height: "48px",
                                                      objectFit: "cover",
                                                      borderRadius: "50%",
                                                      border:
                                                        "1px solid #dee2e6",
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: "48px",
                                                      height: "48px",
                                                      borderRadius: "50%",
                                                      background: "#e9ecef",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      color: "#adb5bd",
                                                      fontSize: "1.2rem",
                                                    }}
                                                  >
                                                    <i className="fas fa-user" />
                                                  </div>
                                                )}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.spouseName || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.primaryPhone || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.secondaryPhone || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.email || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.address || "-"}
                                              </td>
                                              {!isFinalizePage && (
                                                <td
                                                  style={{
                                                    padding: "0.65rem",
                                                    border: "1px solid #dee2e6",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleEditExSpouse(spouse)
                                                    }
                                                    title="Edit Ex-Spouse"
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      marginRight: "0.4rem",
                                                      fontSize: "1rem",
                                                    }}
                                                  >
                                                    ✏️
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleDeleteExSpouse(
                                                        spouse,
                                                      )
                                                    }
                                                    title="Delete Ex-Spouse"
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      fontSize: "1rem",
                                                    }}
                                                  >
                                                    🗑️
                                                  </button>
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div style={{ color: "#6c757d" }}>
                                      No ex-spouse records found.
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>

                          <div style={{ marginBottom: "1.5rem" }}>
                            <div
                              onClick={() => {
                                if (!canManageGroomSpouses) return;
                                setCurrentSpousesAccordionOpen(
                                  !currentSpousesAccordionOpen,
                                );
                              }}
                              style={{
                                cursor: canManageGroomSpouses
                                  ? "pointer"
                                  : "not-allowed",
                                padding: "0.85rem 1rem",
                                background: canManageGroomSpouses
                                  ? "#e8f0fe"
                                  : "#f1f3f5",
                                border: canManageGroomSpouses
                                  ? "1px solid #c5d5f5"
                                  : "1px solid #dee2e6",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontWeight: "600",
                                fontSize: "0.95rem",
                                color: canManageGroomSpouses
                                  ? "inherit"
                                  : "#6c757d",
                              }}
                            >
                              <span>Current Spouse(s)</span>
                              <span>
                                {currentSpousesAccordionOpen ? "▼" : "▶"}
                              </span>
                            </div>
                            {!canManageGroomSpouses && (
                              <div
                                style={{
                                  marginTop: "0.5rem",
                                  color: "#6c757d",
                                  fontSize: "0.85rem",
                                }}
                              >
                                Save groom details first to manage current
                                spouse records.
                              </div>
                            )}
                            {canManageGroomSpouses &&
                              currentSpousesAccordionOpen && (
                                <div
                                  style={{
                                    border: "1px solid #dee2e6",
                                    borderTop: "none",
                                    padding: "1rem",
                                    borderRadius: "0 0 4px 4px",
                                  }}
                                >
                                  {!isFinalizePage && (
                                    <div style={{ marginBottom: "1rem" }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingCurrentSpouseId(null);
                                            setCurrentSpouseFormData({
                                              certificateApplicationId:
                                                application?.id || Number(id),
                                              spouseName: "",
                                              primaryPhone: "",
                                              secondaryPhone: "",
                                              email: "",
                                              address: "",
                                              profileImagePath: "",
                                            });
                                            setCurrentSpouseValidationErrors(
                                              {},
                                            );
                                            setCurrentSpousePhotoFile(null);
                                            setCurrentSpousePhotoPreview(null);
                                            setShowCurrentSpouseForm(true);
                                          }}
                                          style={{
                                            padding: "0.55rem 1rem",
                                            background: "#007bff",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Add New Spouse
                                        </button>
                                      </div>

                                      {false && (
                                        <div style={{ marginTop: "1rem" }}>
                                          <div className="detail-form-grid-responsive">
                                            <div
                                              className={`form-group ${currentSpouseFormData.spouseName ? "has-value" : ""} ${currentSpouseValidationErrors.spouseName ? "form-group--error" : ""}`}
                                            >
                                              <label className="form-label">
                                                Spouse Name *
                                              </label>
                                              <input
                                                type="text"
                                                className={`form-input ${currentSpouseValidationErrors.spouseName ? "form-input--error" : ""}`}
                                                name="spouseName"
                                                value={
                                                  currentSpouseFormData.spouseName
                                                }
                                                onChange={
                                                  handleCurrentSpouseFormChange
                                                }
                                                placeholder=" "
                                              />
                                              {currentSpouseValidationErrors.spouseName && (
                                                <div className="field-error">
                                                  {
                                                    currentSpouseValidationErrors.spouseName
                                                  }
                                                </div>
                                              )}
                                            </div>

                                            <div
                                              className={`form-group ${currentSpouseFormData.primaryPhone ? "has-value" : ""} ${currentSpouseValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                                            >
                                              <label className="form-label">
                                                Primary Phone *
                                              </label>
                                              <input
                                                type="text"
                                                className={`form-input ${currentSpouseValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                                                name="primaryPhone"
                                                value={
                                                  currentSpouseFormData.primaryPhone
                                                }
                                                onChange={
                                                  handleCurrentSpouseFormChange
                                                }
                                                placeholder=" "
                                              />
                                              {currentSpouseValidationErrors.primaryPhone && (
                                                <div className="field-error">
                                                  {
                                                    currentSpouseValidationErrors.primaryPhone
                                                  }
                                                </div>
                                              )}
                                            </div>

                                            <div
                                              className={`form-group ${currentSpouseFormData.secondaryPhone ? "has-value" : ""} ${currentSpouseValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                                            >
                                              <label className="form-label">
                                                Secondary Phone
                                              </label>
                                              <input
                                                type="text"
                                                className={`form-input ${currentSpouseValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                                                name="secondaryPhone"
                                                value={
                                                  currentSpouseFormData.secondaryPhone
                                                }
                                                onChange={
                                                  handleCurrentSpouseFormChange
                                                }
                                                placeholder=" "
                                              />
                                              {currentSpouseValidationErrors.secondaryPhone && (
                                                <div className="field-error">
                                                  {
                                                    currentSpouseValidationErrors.secondaryPhone
                                                  }
                                                </div>
                                              )}
                                            </div>

                                            <div
                                              className={`form-group ${currentSpouseFormData.email ? "has-value" : ""} ${currentSpouseValidationErrors.email ? "form-group--error" : ""}`}
                                            >
                                              <label className="form-label">
                                                Email
                                              </label>
                                              <input
                                                type="email"
                                                className={`form-input ${currentSpouseValidationErrors.email ? "form-input--error" : ""}`}
                                                name="email"
                                                value={
                                                  currentSpouseFormData.email
                                                }
                                                onChange={
                                                  handleCurrentSpouseFormChange
                                                }
                                                placeholder=" "
                                              />
                                              {currentSpouseValidationErrors.email && (
                                                <div className="field-error">
                                                  {
                                                    currentSpouseValidationErrors.email
                                                  }
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          <div
                                            className={`form-group ${currentSpouseFormData.address ? "has-value" : ""} ${currentSpouseValidationErrors.address ? "form-group--error" : ""}`}
                                            style={{ marginBottom: "1rem" }}
                                          >
                                            <label className="form-label">
                                              Address *
                                            </label>
                                            <textarea
                                              className={`form-input ${currentSpouseValidationErrors.address ? "form-input--error" : ""}`}
                                              name="address"
                                              value={
                                                currentSpouseFormData.address
                                              }
                                              onChange={
                                                handleCurrentSpouseFormChange
                                              }
                                              placeholder=" "
                                              rows={3}
                                            />
                                            {currentSpouseValidationErrors.address && (
                                              <div className="field-error">
                                                {
                                                  currentSpouseValidationErrors.address
                                                }
                                              </div>
                                            )}
                                          </div>

                                          <div
                                            style={{
                                              display: "flex",
                                              justifyContent: "flex-end",
                                            }}
                                          >
                                            <button
                                              type="button"
                                              onClick={
                                                handleCreateCurrentSpouse
                                              }
                                              disabled={currentSpouseSubmitting}
                                              style={{
                                                padding: "0.55rem 1rem",
                                                background:
                                                  currentSpouseSubmitting
                                                    ? "#ccc"
                                                    : "#007bff",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "4px",
                                                cursor: currentSpouseSubmitting
                                                  ? "not-allowed"
                                                  : "pointer",
                                                fontWeight: 500,
                                              }}
                                            >
                                              {currentSpouseSubmitting
                                                ? "Saving..."
                                                : editingCurrentSpouseId
                                                  ? "Update Current Spouse"
                                                  : "Save Current Spouse"}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {currentSpouseLoading ? (
                                    <div style={{ color: "#6c757d" }}>
                                      Loading current spouses...
                                    </div>
                                  ) : currentSpouses.length > 0 ? (
                                    <div style={{ overflowX: "auto" }}>
                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          background: "#fff",
                                        }}
                                      >
                                        <thead>
                                          <tr style={{ background: "#f8f9fa" }}>
                                            <th
                                              style={{
                                                textAlign: "center",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                                width: "70px",
                                              }}
                                            >
                                              Photo
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Spouse Name
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Primary Phone
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Secondary Phone
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Email
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Address
                                            </th>
                                            {!isFinalizePage && (
                                              <th
                                                style={{
                                                  textAlign: "center",
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                  width: "90px",
                                                }}
                                              >
                                                Actions
                                              </th>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {currentSpouses.map((spouse) => (
                                            <tr key={spouse.id}>
                                              <td
                                                style={{
                                                  padding: "0.4rem",
                                                  border: "1px solid #dee2e6",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {spouse.photoUrl ? (
                                                  <img
                                                    src={
                                                      getFullPath(
                                                        spouse.photoUrl,
                                                      ) || undefined
                                                    }
                                                    alt={
                                                      spouse.spouseName ||
                                                      "Spouse"
                                                    }
                                                    style={{
                                                      width: "48px",
                                                      height: "48px",
                                                      objectFit: "cover",
                                                      borderRadius: "50%",
                                                      border:
                                                        "1px solid #dee2e6",
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: "48px",
                                                      height: "48px",
                                                      borderRadius: "50%",
                                                      background: "#e9ecef",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      color: "#adb5bd",
                                                      fontSize: "1.2rem",
                                                    }}
                                                  >
                                                    <i className="fas fa-user" />
                                                  </div>
                                                )}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.spouseName || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.primaryPhone || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.secondaryPhone || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.email || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.address || "-"}
                                              </td>
                                              {!isFinalizePage && (
                                                <td
                                                  style={{
                                                    padding: "0.65rem",
                                                    border: "1px solid #dee2e6",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleEditCurrentSpouse(
                                                        spouse,
                                                      )
                                                    }
                                                    title="Edit Current Spouse"
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      marginRight: "0.4rem",
                                                      fontSize: "1rem",
                                                    }}
                                                  >
                                                    ✏️
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleDeleteCurrentSpouse(
                                                        spouse,
                                                      )
                                                    }
                                                    title="Delete Current Spouse"
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      fontSize: "1rem",
                                                    }}
                                                  >
                                                    🗑️
                                                  </button>
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div style={{ color: "#6c757d" }}>
                                      No current spouse records found.
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Bride's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() => setBrideAccordionOpen(!brideAccordionOpen)}
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Bride's Detail</span>
                      <span>{brideAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {brideAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleBrideSubmit}>
                          {/* Photo Upload */}
                          <div
                            style={{
                              marginBottom: "1.5rem",
                            }}
                          >
                            <label
                              style={{
                                display: "block",
                                marginBottom: "0.5rem",
                                fontWeight: "500",
                                fontSize: "0.875rem",
                                color: "#374151",
                              }}
                            >
                              Passport Size Photo{" "}
                              <span style={{ color: "red" }}>*</span>
                            </label>
                            <div className="photo-upload-container">
                              {bridePhotoPreview ? (
                                <div className="photo-preview photo-preview--spread">
                                  <img
                                    src={bridePhotoPreview || undefined}
                                    alt="Bride Preview"
                                    className="preview-image"
                                    onError={(e) => {
                                      const img = e.target as HTMLImageElement;
                                      img.style.border = "2px solid red";
                                      img.alt = "Failed to load image";
                                    }}
                                  />
                                  {!isFinalizePage && (
                                    <button
                                      type="button"
                                      className="btn btn--sm btn--danger"
                                      onClick={removeBridePhoto}
                                    >
                                      Remove Photo
                                    </button>
                                  )}
                                </div>
                              ) : (
                                !isFinalizePage && (
                                  <div className="photo-upload-area">
                                    <input
                                      type="file"
                                      id="bridePhotoUpload"
                                      accept="image/*"
                                      onChange={handleBridePhotoChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="bridePhotoUpload"
                                      className="photo-upload-label"
                                    >
                                      <div className="upload-icon">📷</div>
                                      <div className="upload-text">
                                        <span className="upload-title">
                                          Click to upload photo
                                        </span>
                                        <span className="upload-subtitle">
                                          PNG, JPG up to 5MB
                                        </span>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}
                            </div>
                            {brideValidationErrors.photo && (
                              <div className="field-error">
                                {brideValidationErrors.photo}
                              </div>
                            )}
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${brideFormData.firstName ? "has-value" : ""} ${brideValidationErrors.firstName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">First Name *</label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.firstName ? "form-input--error" : ""}`}
                                name="firstName"
                                value={brideFormData.firstName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.firstName && (
                                <div className="field-error">
                                  {brideValidationErrors.firstName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.middleName ? "has-value" : ""}`}
                            >
                              <label className="form-label">Middle Name</label>
                              <input
                                type="text"
                                className="form-input"
                                name="middleName"
                                value={brideFormData.middleName}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${brideFormData.lastName ? "has-value" : ""} ${brideValidationErrors.lastName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">Last Name *</label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.lastName ? "form-input--error" : ""}`}
                                name="lastName"
                                value={brideFormData.lastName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.lastName && (
                                <div className="field-error">
                                  {brideValidationErrors.lastName}
                                </div>
                              )}
                            </div>
                            <div className="form-group has-value">
                              <label className="form-label">Gender *</label>
                              <select
                                className="form-input"
                                name="genderId"
                                value={brideFormData.genderId}
                                onChange={handleBrideFormChange}
                                required
                                disabled
                                style={{
                                  background: "#f1f1f1",
                                  cursor: "not-allowed",
                                }}
                              >
                                <option value={1}>Male</option>
                                <option value={2}>Female</option>
                              </select>
                            </div>
                            <div
                              className={`form-group has-value ${brideValidationErrors.dateOfBirth ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Date of Birth *
                              </label>
                              <input
                                type="date"
                                className={`form-input ${brideValidationErrors.dateOfBirth ? "form-input--error" : ""}`}
                                name="dateOfBirth"
                                value={brideFormData.dateOfBirth}
                                onChange={handleBrideFormChange}
                                max={getMaxDateForAge(brideMinAge)}
                                required
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.dateOfBirth && (
                                <div className="field-error">
                                  {brideValidationErrors.dateOfBirth}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.nationalIdNumber ? "has-value" : ""}`}
                            >
                              <label className="form-label">
                                National ID Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                name="nationalIdNumber"
                                value={brideFormData.nationalIdNumber}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>
                            <div
                              className={`form-group ${brideFormData.primaryPhone ? "has-value" : ""} ${brideValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Primary Phone *
                              </label>
                              <input
                                type="tel"
                                className={`form-input ${brideValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                                name="primaryPhone"
                                value={brideFormData.primaryPhone}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.primaryPhone && (
                                <div className="field-error">
                                  {brideValidationErrors.primaryPhone}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.secondaryPhone ? "has-value" : ""} ${brideValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Secondary Phone
                              </label>
                              <input
                                type="tel"
                                className={`form-input ${brideValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                                name="secondaryPhone"
                                value={brideFormData.secondaryPhone}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.secondaryPhone && (
                                <div className="field-error">
                                  {brideValidationErrors.secondaryPhone}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Origin & Birth Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group has-value ${brideValidationErrors.countyOfOriginId ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                County of Origin *
                              </label>
                              <select
                                className={`form-input ${brideValidationErrors.countyOfOriginId ? "form-input--error" : ""}`}
                                name="countyOfOriginId"
                                value={brideFormData.countyOfOriginId}
                                onChange={handleBrideFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {brideValidationErrors.countyOfOriginId && (
                                <div className="field-error">
                                  {brideValidationErrors.countyOfOriginId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group has-value ${brideValidationErrors.birthCountyId ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Birth County *
                              </label>
                              <select
                                className={`form-input ${brideValidationErrors.birthCountyId ? "form-input--error" : ""}`}
                                name="birthCountyId"
                                value={brideFormData.birthCountyId}
                                onChange={handleBrideFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select County</option>
                                {counties.map((county) => (
                                  <option key={county.id} value={county.id}>
                                    {county.name}
                                  </option>
                                ))}
                              </select>
                              {brideValidationErrors.birthCountyId && (
                                <div className="field-error">
                                  {brideValidationErrors.birthCountyId}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.birthTown ? "has-value" : ""} ${brideValidationErrors.birthTown ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">Birth Town *</label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.birthTown ? "form-input--error" : ""}`}
                                name="birthTown"
                                value={brideFormData.birthTown}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.birthTown && (
                                <div className="field-error">
                                  {brideValidationErrors.birthTown}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group has-value ${brideValidationErrors.nationalityId ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Nationality *
                              </label>
                              <select
                                className={`form-input ${brideValidationErrors.nationalityId ? "form-input--error" : ""}`}
                                name="nationalityId"
                                value={brideFormData.nationalityId}
                                onChange={handleBrideFormChange}
                                required
                                disabled={isFinalizePage}
                              >
                                <option value={0}>Select Nationality</option>
                                {nationalities.map((nationality) => (
                                  <option
                                    key={nationality.id}
                                    value={nationality.id}
                                  >
                                    {nationality.name}
                                  </option>
                                ))}
                              </select>
                              {brideValidationErrors.nationalityId && (
                                <div className="field-error">
                                  {brideValidationErrors.nationalityId}
                                </div>
                              )}
                            </div>
                          </div>

                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${brideFormData.email ? "has-value" : ""}`}
                            >
                              <label className="form-label">Email</label>
                              <input
                                type="email"
                                className="form-input"
                                name="email"
                                value={brideFormData.email}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                            </div>

                            <div
                              className={`form-group ${brideFormData.brideProposedName ? "has-value" : ""} ${brideValidationErrors.brideProposedName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Bride Proposed Name
                              </label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.brideProposedName ? "form-input--error" : ""}`}
                                name="brideProposedName"
                                value={brideFormData.brideProposedName || ""}
                                onChange={handleBrideFormChange}
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.brideProposedName && (
                                <div className="field-error">
                                  {brideValidationErrors.brideProposedName}
                                </div>
                              )}
                            </div>

                            <div
                              className={`form-group ${brideFormData.currentAddress ? "has-value" : ""} ${brideValidationErrors.currentAddress ? "form-group--error" : ""}`}
                              style={{ gridColumn: "span 2" }}
                            >
                              <label className="form-label">
                                Current Address *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.currentAddress ? "form-input--error" : ""}`}
                                name="currentAddress"
                                value={brideFormData.currentAddress}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.currentAddress && (
                                <div className="field-error">
                                  {brideValidationErrors.currentAddress}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Family Information */}
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${brideFormData.fatherName ? "has-value" : ""} ${brideValidationErrors.fatherName ? "form-group--error" : ""}`}
                              style={{ gridColumn: "span 2" }}
                            >
                              <label className="form-label">
                                Father's Name *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.fatherName ? "form-input--error" : ""}`}
                                name="fatherName"
                                value={brideFormData.fatherName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.fatherName && (
                                <div className="field-error">
                                  {brideValidationErrors.fatherName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${brideFormData.motherName ? "has-value" : ""} ${brideValidationErrors.motherName ? "form-group--error" : ""}`}
                              style={{ gridColumn: "span 2" }}
                            >
                              <label className="form-label">
                                Mother's Name *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${brideValidationErrors.motherName ? "form-input--error" : ""}`}
                                name="motherName"
                                value={brideFormData.motherName}
                                onChange={handleBrideFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {brideValidationErrors.motherName && (
                                <div className="field-error">
                                  {brideValidationErrors.motherName}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <>
                              {declarationText && (
                                <div style={{ marginBottom: "0.85rem" }}>
                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: "0.6rem",
                                      cursor: "pointer",
                                      color: "#495057",
                                      fontSize: "0.9rem",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={brideDeclarationChecked}
                                      onChange={(e) =>
                                        setBrideDeclarationChecked(
                                          e.target.checked,
                                        )
                                      }
                                      style={{ marginTop: "0.15rem" }}
                                    />
                                    <span>{declarationText}</span>
                                  </label>
                                </div>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  gap: "0.75rem",
                                  justifyContent: "flex-end",
                                }}
                              >
                                {brideApplicant && (
                                  <button
                                    type="button"
                                    onClick={handleDeleteBride}
                                    disabled={brideLoading}
                                    style={{
                                      padding: "0.65rem 1.5rem",
                                      background: brideLoading
                                        ? "#ccc"
                                        : "#dc3545",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor: brideLoading
                                        ? "not-allowed"
                                        : "pointer",
                                      fontWeight: 500,
                                    }}
                                  >
                                    Delete Bride
                                  </button>
                                )}
                                <button
                                  type="submit"
                                  disabled={
                                    brideLoading ||
                                    (declarationText
                                      ? !brideDeclarationChecked
                                      : false)
                                  }
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: brideLoading
                                      ? "#ccc"
                                      : declarationText &&
                                          !brideDeclarationChecked
                                        ? "#ccc"
                                        : "#007bff",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: brideLoading
                                      ? "not-allowed"
                                      : declarationText &&
                                          !brideDeclarationChecked
                                        ? "not-allowed"
                                        : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  {brideLoading
                                    ? "Saving..."
                                    : brideApplicant
                                      ? "Update Bride"
                                      : "Save Bride"}
                                </button>
                              </div>
                            </>
                          )}

                          {/* Ex-Spouse or Spouses Accordion */}
                          <div
                            style={{
                              marginTop: "1.5rem",
                              marginBottom: "1.5rem",
                            }}
                          >
                            <div
                              onClick={() => {
                                if (!canManageBrideSpouses) return;
                                setBrideExSpousesAccordionOpen(
                                  !brideExSpousesAccordionOpen,
                                );
                              }}
                              style={{
                                cursor: canManageBrideSpouses
                                  ? "pointer"
                                  : "not-allowed",
                                padding: "0.85rem 1rem",
                                background: canManageBrideSpouses
                                  ? "#e8f0fe"
                                  : "#f1f3f5",
                                border: canManageBrideSpouses
                                  ? "1px solid #c5d5f5"
                                  : "1px solid #dee2e6",
                                borderRadius: "4px",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontWeight: "600",
                                fontSize: "0.95rem",
                                color: canManageBrideSpouses
                                  ? "inherit"
                                  : "#6c757d",
                              }}
                            >
                              <span>Ex-Spouse or Spouses</span>
                              <span>
                                {brideExSpousesAccordionOpen ? "▼" : "▶"}
                              </span>
                            </div>
                            {!canManageBrideSpouses && (
                              <div
                                style={{
                                  marginTop: "0.5rem",
                                  color: "#6c757d",
                                  fontSize: "0.85rem",
                                }}
                              >
                                Save bride details first to manage ex-spouse
                                records.
                              </div>
                            )}
                            {canManageBrideSpouses &&
                              brideExSpousesAccordionOpen && (
                                <div
                                  style={{
                                    border: "1px solid #dee2e6",
                                    borderTop: "none",
                                    padding: "1rem",
                                    borderRadius: "0 0 4px 4px",
                                  }}
                                >
                                  {!isFinalizePage && (
                                    <div style={{ marginBottom: "1rem" }}>
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "flex-end",
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditingExSpouseId(null);
                                            setExSpouseFormData({
                                              certificateApplicationId:
                                                application?.id || Number(id),
                                              isGroomEx: false,
                                              spouseName: "",
                                              primaryPhone: "",
                                              secondaryPhone: "",
                                              email: "",
                                              address: "",
                                              profileImagePath: "",
                                            });
                                            setExSpouseValidationErrors({});
                                            setExSpousePhotoFile(null);
                                            setExSpousePhotoPreview(null);
                                            setShowExSpouseForm(true);
                                          }}
                                          style={{
                                            padding: "0.55rem 1rem",
                                            background: "#007bff",
                                            color: "#fff",
                                            border: "none",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontWeight: 500,
                                          }}
                                        >
                                          Add Ex-Spouse
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {exSpouseLoading ? (
                                    <div style={{ color: "#6c757d" }}>
                                      Loading ex-spouses...
                                    </div>
                                  ) : brideExSpouses.length > 0 ? (
                                    <div style={{ overflowX: "auto" }}>
                                      <table
                                        style={{
                                          width: "100%",
                                          borderCollapse: "collapse",
                                          background: "#fff",
                                        }}
                                      >
                                        <thead>
                                          <tr style={{ background: "#f8f9fa" }}>
                                            <th
                                              style={{
                                                textAlign: "center",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                                width: "70px",
                                              }}
                                            >
                                              Photo
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Spouse Name
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Primary Phone
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Secondary Phone
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Email
                                            </th>
                                            <th
                                              style={{
                                                textAlign: "left",
                                                padding: "0.65rem",
                                                border: "1px solid #dee2e6",
                                              }}
                                            >
                                              Address
                                            </th>
                                            {!isFinalizePage && (
                                              <th
                                                style={{
                                                  textAlign: "center",
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                  width: "90px",
                                                }}
                                              >
                                                Actions
                                              </th>
                                            )}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {brideExSpouses.map((spouse) => (
                                            <tr key={spouse.id}>
                                              <td
                                                style={{
                                                  padding: "0.4rem",
                                                  border: "1px solid #dee2e6",
                                                  textAlign: "center",
                                                }}
                                              >
                                                {spouse.photoUrl ? (
                                                  <img
                                                    src={
                                                      getFullPath(
                                                        spouse.photoUrl,
                                                      ) || undefined
                                                    }
                                                    alt={
                                                      spouse.spouseName ||
                                                      "Ex-Spouse"
                                                    }
                                                    style={{
                                                      width: "48px",
                                                      height: "48px",
                                                      objectFit: "cover",
                                                      borderRadius: "50%",
                                                      border:
                                                        "1px solid #dee2e6",
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: "48px",
                                                      height: "48px",
                                                      borderRadius: "50%",
                                                      background: "#e9ecef",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      color: "#adb5bd",
                                                      fontSize: "1.2rem",
                                                    }}
                                                  >
                                                    <i className="fas fa-user" />
                                                  </div>
                                                )}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.spouseName || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.primaryPhone || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.secondaryPhone || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.email || "-"}
                                              </td>
                                              <td
                                                style={{
                                                  padding: "0.65rem",
                                                  border: "1px solid #dee2e6",
                                                }}
                                              >
                                                {spouse.address || "-"}
                                              </td>
                                              {!isFinalizePage && (
                                                <td
                                                  style={{
                                                    padding: "0.65rem",
                                                    border: "1px solid #dee2e6",
                                                    textAlign: "center",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleEditExSpouse(spouse)
                                                    }
                                                    title="Edit Ex-Spouse"
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      marginRight: "0.4rem",
                                                      fontSize: "1rem",
                                                    }}
                                                  >
                                                    ✏️
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      handleDeleteExSpouse(
                                                        spouse,
                                                      )
                                                    }
                                                    title="Delete Ex-Spouse"
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      fontSize: "1rem",
                                                    }}
                                                  >
                                                    🗑️
                                                  </button>
                                                </td>
                                              )}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div style={{ color: "#6c757d" }}>
                                      No ex-spouse records found.
                                    </div>
                                  )}
                                </div>
                              )}
                          </div>
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Officiator's Detail Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setOfficiatorAccordionOpen(!officiatorAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Officiator's Detail</span>
                      <span>{officiatorAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {officiatorAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        <form onSubmit={handleOfficiatorSubmit}>
                          <div
                            className="detail-form-grid-responsive"
                            style={{ marginBottom: "1.5rem" }}
                          >
                            <div
                              className={`form-group ${officiatorFormData.officiatorName ? "has-value" : ""} ${officiatorValidationErrors.officiatorName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Officiator Name *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${officiatorValidationErrors.officiatorName ? "form-input--error" : ""}`}
                                name="officiatorName"
                                value={officiatorFormData.officiatorName}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.officiatorName && (
                                <div className="field-error">
                                  {officiatorValidationErrors.officiatorName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${officiatorFormData.officiatorContact ? "has-value" : ""} ${officiatorValidationErrors.officiatorContact ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Officiator Contact *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${officiatorValidationErrors.officiatorContact ? "form-input--error" : ""}`}
                                name="officiatorContact"
                                value={officiatorFormData.officiatorContact}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.officiatorContact && (
                                <div className="field-error">
                                  {officiatorValidationErrors.officiatorContact}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${officiatorFormData.witnessName ? "has-value" : ""} ${officiatorValidationErrors.witnessName ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Witness Name *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${officiatorValidationErrors.witnessName ? "form-input--error" : ""}`}
                                name="witnessName"
                                value={officiatorFormData.witnessName}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.witnessName && (
                                <div className="field-error">
                                  {officiatorValidationErrors.witnessName}
                                </div>
                              )}
                            </div>
                            <div
                              className={`form-group ${officiatorFormData.witnessContact ? "has-value" : ""} ${officiatorValidationErrors.witnessContact ? "form-group--error" : ""}`}
                            >
                              <label className="form-label">
                                Witness Contact *
                              </label>
                              <input
                                type="text"
                                className={`form-input ${officiatorValidationErrors.witnessContact ? "form-input--error" : ""}`}
                                name="witnessContact"
                                value={officiatorFormData.witnessContact}
                                onChange={handleOfficiatorFormChange}
                                required
                                placeholder=" "
                                readOnly={isFinalizePage}
                              />
                              {officiatorValidationErrors.witnessContact && (
                                <div className="field-error">
                                  {officiatorValidationErrors.witnessContact}
                                </div>
                              )}
                            </div>
                          </div>

                          {!isFinalizePage && (
                            <div
                              style={{
                                display: "flex",
                                gap: "0.75rem",
                                justifyContent: "flex-end",
                              }}
                            >
                              {officiator && (
                                <button
                                  type="button"
                                  onClick={handleDeleteOfficiator}
                                  disabled={officiatorLoading}
                                  style={{
                                    padding: "0.65rem 1.5rem",
                                    background: officiatorLoading
                                      ? "#ccc"
                                      : "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: officiatorLoading
                                      ? "not-allowed"
                                      : "pointer",
                                    fontWeight: 500,
                                  }}
                                >
                                  Delete Officiator
                                </button>
                              )}
                              <button
                                type="submit"
                                disabled={officiatorLoading}
                                style={{
                                  padding: "0.65rem 1.5rem",
                                  background: officiatorLoading
                                    ? "#ccc"
                                    : "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: officiatorLoading
                                    ? "not-allowed"
                                    : "pointer",
                                  fontWeight: 500,
                                }}
                              >
                                {officiatorLoading
                                  ? "Saving..."
                                  : officiator
                                    ? "Update Officiator"
                                    : "Save Officiator"}
                              </button>
                            </div>
                          )}
                        </form>
                      </div>
                    )}
                  </div>
                )}

                {/* Attachments Accordion */}
                {!isPrintPage && (
                  <div style={{ marginTop: "1rem" }}>
                    <div
                      onClick={() =>
                        setAttachmentsAccordionOpen(!attachmentsAccordionOpen)
                      }
                      style={{
                        cursor: "pointer",
                        padding: "1rem",
                        background: "#f8f9fa",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontWeight: "600",
                        fontSize: "1rem",
                      }}
                    >
                      <span>Attachments</span>
                      <span>{attachmentsAccordionOpen ? "▼" : "▶"}</span>
                    </div>
                    {attachmentsAccordionOpen && (
                      <div
                        style={{
                          border: "1px solid #dee2e6",
                          borderTop: "none",
                          padding: "1.5rem",
                          borderRadius: "0 0 4px 4px",
                        }}
                      >
                        {showDocumentScanModal && application && (
                          <DocumentScan
                            application={application}
                            title="Upload Required Document"
                            hideDocumentType
                            defaultDocumentTypeId={
                              selectedDocumentTypeId || undefined
                            }
                            onClose={() => {
                              setShowDocumentScanModal(false);
                              setSelectedDocumentTypeId(null);
                            }}
                            onScanSuccess={() => {
                              fetchData();
                              fetchAttachments();
                              setShowDocumentScanModal(false);
                              setSelectedDocumentTypeId(null);
                            }}
                          />
                        )}
                        {!showDocumentScanModal && (
                          <>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "1rem",
                              }}
                            >
                              <h4
                                style={{
                                  margin: 0,
                                  fontSize: "0.95rem",
                                  fontWeight: "600",
                                  color: "#374151",
                                }}
                              >
                                Required Documents ({attachments.length})
                              </h4>
                            </div>

                            {attachmentsLoading ? (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  color: "#6b7280",
                                }}
                              >
                                Loading attachments...
                              </div>
                            ) : attachments.length > 0 ? (
                              <div style={{ overflowX: "auto" }}>
                                <table
                                  style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  <thead>
                                    <tr
                                      style={{
                                        background: "#f8f9fa",
                                        borderBottom: "2px solid #dee2e6",
                                      }}
                                    >
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Document Type
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Required
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "left",
                                          fontWeight: "600",
                                          color: "#374151",
                                        }}
                                      >
                                        Uploaded
                                      </th>
                                      <th
                                        style={{
                                          padding: "0.75rem",
                                          textAlign: "center",
                                          fontWeight: "600",
                                          color: "#374151",
                                          width: "140px",
                                        }}
                                      >
                                        Action
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {attachments.map((attachment) => (
                                      <React.Fragment
                                        key={attachment.requiredDocumentId}
                                      >
                                        <tr
                                          style={{
                                            borderBottom:
                                              viewingAttachmentId ===
                                              attachment.requiredDocumentId
                                                ? "none"
                                                : "1px solid #dee2e6",
                                          }}
                                        >
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.documentTypeName || "-"}
                                          </td>
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.isRequired
                                              ? "Yes"
                                              : "No"}
                                          </td>
                                          <td style={{ padding: "0.75rem" }}>
                                            {attachment.isUploaded
                                              ? "Yes"
                                              : "No"}
                                          </td>
                                          <td
                                            style={{
                                              padding: "0.75rem",
                                              textAlign: "center",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "0.5rem",
                                                justifyContent: "center",
                                              }}
                                            >
                                              {attachment.isUploaded ? (
                                                <>
                                                  <button
                                                    onClick={() => {
                                                      const newId =
                                                        viewingAttachmentId ===
                                                        attachment.requiredDocumentId
                                                          ? null
                                                          : attachment.requiredDocumentId;
                                                      setViewingAttachmentId(
                                                        newId,
                                                      );
                                                    }}
                                                    title={
                                                      viewingAttachmentId ===
                                                      attachment.requiredDocumentId
                                                        ? "Hide document"
                                                        : "Read document"
                                                    }
                                                    style={{
                                                      background: "transparent",
                                                      border: "none",
                                                      cursor: "pointer",
                                                      fontSize: "1.25rem",
                                                      padding: "0.25rem",
                                                      color:
                                                        viewingAttachmentId ===
                                                        attachment.requiredDocumentId
                                                          ? "#10b981"
                                                          : "#3b82f6",
                                                    }}
                                                  >
                                                    {viewingAttachmentId ===
                                                    attachment.requiredDocumentId
                                                      ? "👁️‍🗨️"
                                                      : "👁️"}
                                                  </button>
                                                  {!isFinalizePage && (
                                                    <button
                                                      onClick={() =>
                                                        handleDeleteAttachment(
                                                          attachment,
                                                        )
                                                      }
                                                      title="Delete attachment"
                                                      style={{
                                                        background:
                                                          "transparent",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        fontSize: "1.25rem",
                                                        padding: "0.25rem",
                                                        color: "#ef4444",
                                                      }}
                                                    >
                                                      🗑️
                                                    </button>
                                                  )}
                                                </>
                                              ) : !isFinalizePage ? (
                                                <button
                                                  className="btn btn--primary"
                                                  onClick={() => {
                                                    setSelectedDocumentTypeId(
                                                      attachment.documentTypeId >
                                                        0
                                                        ? attachment.documentTypeId
                                                        : null,
                                                    );
                                                    setShowDocumentScanModal(
                                                      true,
                                                    );
                                                  }}
                                                  style={{
                                                    padding: "0.25rem 0.75rem",
                                                  }}
                                                >
                                                  Upload
                                                </button>
                                              ) : (
                                                <span>-</span>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                        {viewingAttachmentId ===
                                          attachment.requiredDocumentId && (
                                          <tr>
                                            <td
                                              colSpan={4}
                                              style={{
                                                padding: 0,
                                                borderBottom:
                                                  "1px solid #dee2e6",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  background: "#f8f9fa",
                                                  padding: "1rem",
                                                  borderTop:
                                                    "2px solid #3b82f6",
                                                }}
                                              >
                                                {attachment.description && (
                                                  <div
                                                    style={{
                                                      marginBottom: "0.75rem",
                                                      padding: "0.5rem",
                                                      background: "#fff",
                                                      borderRadius: "4px",
                                                      fontSize: "0.875rem",
                                                      color: "#6b7280",
                                                    }}
                                                  >
                                                    <strong>
                                                      Description:
                                                    </strong>{" "}
                                                    {attachment.description}
                                                  </div>
                                                )}
                                                {(() => {
                                                  if (!attachment.isUploaded) {
                                                    return null;
                                                  }

                                                  const resolvedDocumentPath =
                                                    attachment.documentPath?.trim() ||
                                                    "";

                                                  if (!resolvedDocumentPath) {
                                                    return (
                                                      <div
                                                        style={{
                                                          padding: "1rem",
                                                          background: "#fee",
                                                          color: "#c00",
                                                          borderRadius: "4px",
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        Document file path is
                                                        missing
                                                      </div>
                                                    );
                                                  }

                                                  const fullUrl =
                                                    getFullPath(
                                                      resolvedDocumentPath,
                                                    );

                                                  return (
                                                    <div>
                                                      <iframe
                                                        key={
                                                          attachment.requiredDocumentId
                                                        }
                                                        src={
                                                          fullUrl || undefined
                                                        }
                                                        title={
                                                          attachment.documentTypeName ||
                                                          "Document"
                                                        }
                                                        style={{
                                                          width: "100%",
                                                          height: "600px",
                                                          border:
                                                            "1px solid #dee2e6",
                                                          borderRadius: "4px",
                                                          background: "#fff",
                                                        }}
                                                      />
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div
                                style={{
                                  textAlign: "center",
                                  padding: "2rem",
                                  background: "#f8f9fa",
                                  borderRadius: "4px",
                                  color: "#6b7280",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "2rem",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  📁
                                </div>
                                <div
                                  style={{
                                    fontWeight: "500",
                                    marginBottom: "0.25rem",
                                  }}
                                >
                                  No required documents found
                                </div>
                                <div style={{ fontSize: "0.875rem" }}>
                                  Configure required documents for this module
                                  to show attachment requirements.
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Column 2: Action Buttons */}
              <div className="user-detail-actions">
                {!isPortal && (
                  <button
                    className="user-detail-back user-detail-back--desktop"
                    onClick={() => navigate(getBackPath())}
                  >
                    ← Back to Applications
                  </button>
                )}

                {isFinalizePage &&
                  groomApplicant &&
                  brideApplicant &&
                  (groomApplicant.photoUrl ||
                    groomApplicant.profileImagePath) &&
                  (brideApplicant.photoUrl ||
                    brideApplicant.profileImagePath) &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.View,
                  ) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={() => navigate(`/marriages/print/${id}`)}
                      title="Print the marriage certificate"
                      style={{
                        background: "#3b82f6",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">🖨️</span> Print Certificate
                    </button>
                  )}

                {!isPrintPage &&
                  signedCertificate &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.View,
                  ) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={() => setShowSignatureExtractionModal(true)}
                      title="Extract signatures from certificate"
                      style={{
                        background: "#007bff",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">✍️</span> Extract Signatures
                      {certificateSignatures.length > 0 && (
                        <span
                          style={{
                            marginLeft: "0.5rem",
                            background: "#fff",
                            color: "#007bff",
                            borderRadius: "999px",
                            padding: "0.1rem 0.45rem",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            lineHeight: 1.4,
                          }}
                        >
                          {certificateSignatures.length}
                        </span>
                      )}
                    </button>
                  )}

                {isPortal && canContinueToSummary && !hasExistingPayment && (
                  <button
                    className="user-detail-action-btn user-detail-action-btn--warning"
                    onClick={openManualPaymentModal}
                    title="Make payment"
                    style={{ background: "#ffc107", color: "#000" }}
                  >
                    <span className="action-icon">💳</span> Make Payment
                  </button>
                )}

                {!isPrintPage &&
                  !isPortal &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Apply,
                  ) &&
                  hasRequiredPaymentDetails &&
                  hasAttachmentsAddedRequirement &&
                  !hasExistingPayment && (
                    <div style={{ position: "relative" }}>
                      <button
                        className="user-detail-action-btn user-detail-action-btn--warning"
                        onClick={() =>
                          setPaymentDropdownOpen(!paymentDropdownOpen)
                        }
                        title="Make payment"
                        style={{
                          background: "#ffc107",
                          color: "#000",
                        }}
                      >
                        <span className="action-icon">💳</span> Make Payment
                        <span style={{ marginLeft: "0.5rem" }}>▼</span>
                      </button>
                      {paymentDropdownOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            marginTop: "0.25rem",
                            background: "#fff",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            zIndex: 1000,
                            overflow: "hidden",
                          }}
                        >
                          <button
                            onClick={() => handlePayment("Manual")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid #dee2e6",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>📝</span>{" "}
                            Manual Payment
                          </button>
                          <button
                            onClick={() => handlePayment("Card")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              borderBottom: "1px solid #dee2e6",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>💳</span>{" "}
                            Card Payment
                          </button>
                          <button
                            onClick={() => handlePayment("Mobile Money")}
                            style={{
                              width: "100%",
                              padding: "0.75rem 1rem",
                              background: "transparent",
                              border: "none",
                              textAlign: "left",
                              cursor: "pointer",
                              fontSize: "0.875rem",
                              transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#f8f9fa")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "transparent")
                            }
                          >
                            <span style={{ marginRight: "0.5rem" }}>📱</span>{" "}
                            Mobile Money
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                {!isPrintPage &&
                  !isPortal &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Edit,
                  ) &&
                  isReadyForSubmission &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--info"
                      onClick={handleSubmit}
                      title="Submit application"
                      style={{
                        background: "#17a2b8",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">📋</span> Submit
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--success"
                      onClick={handleApprove}
                      title="Approve application"
                      style={{
                        background: "#28a745",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">✅</span> Approve
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--warning"
                      onClick={handleQuery}
                      title="Query application"
                      style={{
                        background: "#ffc107",
                        color: "#000",
                      }}
                    >
                      <span className="action-icon">❓</span> Query
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Approve,
                  ) &&
                  application?.workflowId &&
                  isAtMyApprovalLevel &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--danger"
                      onClick={handleReject}
                      title="Reject application"
                      style={{
                        background: "#dc3545",
                        color: "#fff",
                      }}
                    >
                      <span className="action-icon">❌</span> Reject
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Edit,
                  ) &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--primary"
                      onClick={openEditModal}
                      title="Edit this application"
                    >
                      <span className="action-icon">✏️</span> Edit
                    </button>
                  )}

                {!isPrintPage &&
                  HasPermission(
                    ModuleId.Weddings,
                    ApplicationPermissionId.Delete,
                  ) &&
                  (application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) && (
                    <button
                      className="user-detail-action-btn user-detail-action-btn--danger"
                      onClick={handleDelete}
                    >
                      <span className="action-icon">🗑️</span> Delete
                    </button>
                  )}

                {!isPortal && (
                  <button
                    className="user-detail-action-btn"
                    onClick={() => setShowHistory(true)}
                  >
                    <span className="action-icon">🕘</span> History
                  </button>
                )}
                {hasQueryRemarks && (
                  <button
                    className="user-detail-action-btn user-detail-action-btn--warning"
                    onClick={() => setShowQueryRemarks(true)}
                  >
                    <span className="action-icon">💬</span> Query Remarks
                  </button>
                )}

                {/* Required to Submit Section */}
                {!isPrintPage &&
                  (isPortal ||
                    application?.statusId === UserStatusId.Created ||
                    application?.statusId === UserStatusId.Queried) &&
                  Object.keys(submissionRequirements).length > 0 && (
                    <div
                      style={{
                        marginTop: "1.5rem",
                        padding: "1rem",
                        background: submitSectionReady
                          ? "#d4edda"
                          : "#f8f9fa",
                        border: `1px solid ${
                          submitSectionReady ? "#c3e6cb" : "#dee2e6"
                        }`,
                        borderRadius: "4px",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 0.75rem 0",
                          fontSize: "0.875rem",
                          fontWeight: "600",
                          color: submitSectionReady ? "#155724" : "#495057",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {submitSectionReady
                          ? "✅ Ready to Submit"
                          : "Required to Submit"}
                      </h4>
                      <div style={{ fontSize: "0.875rem" }}>
                        {Object.entries(submissionRequirements)
                          .filter(
                            ([key]) =>
                              !(
                                isPortal &&
                                key.toLowerCase().includes("payment")
                              ),
                          )
                          .map(([key, value]) => {
                            // Format the key for display
                            const displayKey = key
                              .replace(/([A-Z])/g, " $1")
                              .replace(/^./, (str) => str.toUpperCase())
                              .trim();

                            return (
                              <div
                                key={key}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  padding: "0.5rem 0",
                                  borderBottom: "1px solid #e9ecef",
                                }}
                              >
                                <span
                                  style={{
                                    marginRight: "0.5rem",
                                    fontSize: "1rem",
                                  }}
                                >
                                  {value ? "✅" : "❌"}
                                </span>
                                <span
                                  style={{
                                    color: value ? "#28a745" : "#6c757d",
                                    fontWeight: value ? "500" : "normal",
                                  }}
                                >
                                  {displayKey}
                                </span>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          )}

          <History
            entityName="CertificateApplications"
            recordId={application?.id}
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
          />

          <QueryRemarks
            entityName="CertificateApplications"
            recordId={application?.id}
            isOpen={showQueryRemarks}
            onClose={() => setShowQueryRemarks(false)}
          />

          <QueryApplicationForm
            isOpen={showQueryForm}
            onClose={() => setShowQueryForm(false)}
            entityName="CertificateApplications"
            recordId={application?.id}
            workflowId={application?.workflowId}
            statusId={application?.statusId}
            onSubmit={handleSubmitQuery}
          />

          {/* Ex-Spouse Side Panel */}
          {showExSpouseForm && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">
                    {editingExSpouseId ? "Edit Ex-Spouse" : "Add Ex-Spouse"}
                  </h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowExSpouseForm(false);
                      setEditingExSpouseId(null);
                      setExSpouseValidationErrors({});
                      setExSpouseFormData({
                        certificateApplicationId: application?.id || Number(id),
                        isGroomEx: true,
                        spouseName: "",
                        primaryPhone: "",
                        secondaryPhone: "",
                        email: "",
                        address: "",
                        profileImagePath: "",
                      });
                      setExSpousePhotoFile(null);
                      setExSpousePhotoPreview(null);
                    }}
                    disabled={exSpouseSubmitting}
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-form">
                  <div style={{ marginBottom: "1.5rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "500",
                        fontSize: "0.875rem",
                        color: "#374151",
                      }}
                    >
                      Passport Size Photo{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <div className="photo-upload-container">
                      {exSpousePhotoPreview ? (
                        <div className="photo-preview photo-preview--spread">
                          <img
                            src={exSpousePhotoPreview || undefined}
                            alt="Ex-Spouse Preview"
                            className="preview-image"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.border = "2px solid red";
                              img.alt = "Failed to load image";
                            }}
                          />
                          {!isFinalizePage && (
                            <button
                              type="button"
                              className="btn btn--sm btn--danger"
                              onClick={removeExSpousePhoto}
                            >
                              Remove Photo
                            </button>
                          )}
                        </div>
                      ) : (
                        !isFinalizePage && (
                          <div className="photo-upload-area">
                            <input
                              type="file"
                              id="exSpousePhotoUpload"
                              accept="image/*"
                              onChange={handleExSpousePhotoChange}
                              style={{ display: "none" }}
                            />
                            <label
                              htmlFor="exSpousePhotoUpload"
                              className="photo-upload-label"
                            >
                              <div className="upload-icon">📷</div>
                              <div className="upload-text">
                                <span className="upload-title">
                                  Click to upload photo
                                </span>
                                <span className="upload-subtitle">
                                  PNG, JPG up to 5MB
                                </span>
                              </div>
                            </label>
                          </div>
                        )
                      )}
                    </div>
                    {exSpouseValidationErrors.photo && (
                      <div className="field-error">
                        {exSpouseValidationErrors.photo}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                    }}
                  >
                    <div
                      className={`form-group ${exSpouseFormData.spouseName ? "has-value" : ""} ${exSpouseValidationErrors.spouseName ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Spouse Name *</label>
                      <input
                        type="text"
                        className={`form-input ${exSpouseValidationErrors.spouseName ? "form-input--error" : ""}`}
                        name="spouseName"
                        value={exSpouseFormData.spouseName}
                        onChange={handleExSpouseFormChange}
                        placeholder=" "
                      />
                      {exSpouseValidationErrors.spouseName && (
                        <div className="field-error">
                          {exSpouseValidationErrors.spouseName}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${exSpouseFormData.primaryPhone ? "has-value" : ""} ${exSpouseValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Primary Phone *</label>
                      <input
                        type="text"
                        className={`form-input ${exSpouseValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                        name="primaryPhone"
                        value={exSpouseFormData.primaryPhone}
                        onChange={handleExSpouseFormChange}
                        placeholder=" "
                      />
                      {exSpouseValidationErrors.primaryPhone && (
                        <div className="field-error">
                          {exSpouseValidationErrors.primaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${exSpouseFormData.secondaryPhone ? "has-value" : ""} ${exSpouseValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Secondary Phone</label>
                      <input
                        type="text"
                        className={`form-input ${exSpouseValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                        name="secondaryPhone"
                        value={exSpouseFormData.secondaryPhone}
                        onChange={handleExSpouseFormChange}
                        placeholder=" "
                      />
                      {exSpouseValidationErrors.secondaryPhone && (
                        <div className="field-error">
                          {exSpouseValidationErrors.secondaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${exSpouseFormData.email ? "has-value" : ""} ${exSpouseValidationErrors.email ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className={`form-input ${exSpouseValidationErrors.email ? "form-input--error" : ""}`}
                        name="email"
                        value={exSpouseFormData.email}
                        onChange={handleExSpouseFormChange}
                        placeholder=" "
                      />
                      {exSpouseValidationErrors.email && (
                        <div className="field-error">
                          {exSpouseValidationErrors.email}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`form-group ${exSpouseFormData.address ? "has-value" : ""} ${exSpouseValidationErrors.address ? "form-group--error" : ""}`}
                    style={{ marginBottom: "1rem", marginTop: "1rem" }}
                  >
                    <label className="form-label">Address *</label>
                    <textarea
                      className={`form-input ${exSpouseValidationErrors.address ? "form-input--error" : ""}`}
                      name="address"
                      value={exSpouseFormData.address}
                      onChange={handleExSpouseFormChange}
                      placeholder=" "
                      rows={3}
                    />
                    {exSpouseValidationErrors.address && (
                      <div className="field-error">
                        {exSpouseValidationErrors.address}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowExSpouseForm(false);
                        setEditingExSpouseId(null);
                        setExSpouseValidationErrors({});
                        setExSpouseFormData({
                          certificateApplicationId:
                            application?.id || Number(id),
                          isGroomEx: true,
                          spouseName: "",
                          primaryPhone: "",
                          secondaryPhone: "",
                          email: "",
                          address: "",
                          profileImagePath: "",
                        });
                        setExSpousePhotoFile(null);
                        setExSpousePhotoPreview(null);
                      }}
                      disabled={exSpouseSubmitting}
                      className="btn btn--outline"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateExSpouse}
                      disabled={exSpouseSubmitting}
                      className="btn btn--primary"
                    >
                      {exSpouseSubmitting ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Saving...
                        </>
                      ) : editingExSpouseId ? (
                        "Update Ex-Spouse"
                      ) : (
                        "Save Ex-Spouse"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Spouse Side Panel */}
          {showCurrentSpouseForm && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">
                    {editingCurrentSpouseId
                      ? "Edit Current Spouse"
                      : "Add New Spouse"}
                  </h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowCurrentSpouseForm(false);
                      setEditingCurrentSpouseId(null);
                      setCurrentSpouseValidationErrors({});
                      setCurrentSpouseFormData({
                        certificateApplicationId: application?.id || Number(id),
                        spouseName: "",
                        primaryPhone: "",
                        secondaryPhone: "",
                        email: "",
                        address: "",
                        profileImagePath: "",
                      });
                      setCurrentSpousePhotoFile(null);
                      setCurrentSpousePhotoPreview(null);
                    }}
                    disabled={currentSpouseSubmitting}
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-form">
                  <div
                    style={{
                      marginBottom: "1.5rem",
                    }}
                  >
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "500",
                        fontSize: "0.875rem",
                        color: "#374151",
                      }}
                    >
                      Passport Size Photo{" "}
                      <span style={{ color: "red" }}>*</span>
                    </label>
                    <div className="photo-upload-container">
                      {currentSpousePhotoPreview ? (
                        <div className="photo-preview photo-preview--spread">
                          <img
                            src={currentSpousePhotoPreview || undefined}
                            alt="Current Spouse Preview"
                            className="preview-image"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.border = "2px solid red";
                              img.alt = "Failed to load image";
                            }}
                          />
                          {!isFinalizePage && (
                            <button
                              type="button"
                              className="btn btn--sm btn--danger"
                              onClick={removeCurrentSpousePhoto}
                            >
                              Remove Photo
                            </button>
                          )}
                        </div>
                      ) : (
                        !isFinalizePage && (
                          <div className="photo-upload-area">
                            <input
                              type="file"
                              id="currentSpousePhotoUpload"
                              accept="image/*"
                              onChange={handleCurrentSpousePhotoChange}
                              style={{ display: "none" }}
                            />
                            <label
                              htmlFor="currentSpousePhotoUpload"
                              className="photo-upload-label"
                            >
                              <div className="upload-icon">📷</div>
                              <div className="upload-text">
                                <span className="upload-title">
                                  Click to upload photo
                                </span>
                                <span className="upload-subtitle">
                                  PNG, JPG up to 5MB
                                </span>
                              </div>
                            </label>
                          </div>
                        )
                      )}
                    </div>
                    {currentSpouseValidationErrors.photo && (
                      <div className="field-error">
                        {currentSpouseValidationErrors.photo}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                    }}
                  >
                    <div
                      className={`form-group ${currentSpouseFormData.spouseName ? "has-value" : ""} ${currentSpouseValidationErrors.spouseName ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Spouse Name *</label>
                      <input
                        type="text"
                        className={`form-input ${currentSpouseValidationErrors.spouseName ? "form-input--error" : ""}`}
                        name="spouseName"
                        value={currentSpouseFormData.spouseName}
                        onChange={handleCurrentSpouseFormChange}
                        placeholder=" "
                      />
                      {currentSpouseValidationErrors.spouseName && (
                        <div className="field-error">
                          {currentSpouseValidationErrors.spouseName}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${currentSpouseFormData.primaryPhone ? "has-value" : ""} ${currentSpouseValidationErrors.primaryPhone ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Primary Phone *</label>
                      <input
                        type="text"
                        className={`form-input ${currentSpouseValidationErrors.primaryPhone ? "form-input--error" : ""}`}
                        name="primaryPhone"
                        value={currentSpouseFormData.primaryPhone}
                        onChange={handleCurrentSpouseFormChange}
                        placeholder=" "
                      />
                      {currentSpouseValidationErrors.primaryPhone && (
                        <div className="field-error">
                          {currentSpouseValidationErrors.primaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${currentSpouseFormData.secondaryPhone ? "has-value" : ""} ${currentSpouseValidationErrors.secondaryPhone ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Secondary Phone</label>
                      <input
                        type="text"
                        className={`form-input ${currentSpouseValidationErrors.secondaryPhone ? "form-input--error" : ""}`}
                        name="secondaryPhone"
                        value={currentSpouseFormData.secondaryPhone}
                        onChange={handleCurrentSpouseFormChange}
                        placeholder=" "
                      />
                      {currentSpouseValidationErrors.secondaryPhone && (
                        <div className="field-error">
                          {currentSpouseValidationErrors.secondaryPhone}
                        </div>
                      )}
                    </div>

                    <div
                      className={`form-group ${currentSpouseFormData.email ? "has-value" : ""} ${currentSpouseValidationErrors.email ? "form-group--error" : ""}`}
                    >
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        className={`form-input ${currentSpouseValidationErrors.email ? "form-input--error" : ""}`}
                        name="email"
                        value={currentSpouseFormData.email}
                        onChange={handleCurrentSpouseFormChange}
                        placeholder=" "
                      />
                      {currentSpouseValidationErrors.email && (
                        <div className="field-error">
                          {currentSpouseValidationErrors.email}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    className={`form-group ${currentSpouseFormData.address ? "has-value" : ""} ${currentSpouseValidationErrors.address ? "form-group--error" : ""}`}
                    style={{ marginBottom: "1rem", marginTop: "1rem" }}
                  >
                    <label className="form-label">Address *</label>
                    <textarea
                      className={`form-input ${currentSpouseValidationErrors.address ? "form-input--error" : ""}`}
                      name="address"
                      value={currentSpouseFormData.address}
                      onChange={handleCurrentSpouseFormChange}
                      placeholder=" "
                      rows={3}
                    />
                    {currentSpouseValidationErrors.address && (
                      <div className="field-error">
                        {currentSpouseValidationErrors.address}
                      </div>
                    )}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCurrentSpouseForm(false);
                        setEditingCurrentSpouseId(null);
                        setCurrentSpouseValidationErrors({});
                        setCurrentSpouseFormData({
                          certificateApplicationId:
                            application?.id || Number(id),
                          spouseName: "",
                          primaryPhone: "",
                          secondaryPhone: "",
                          email: "",
                          address: "",
                          profileImagePath: "",
                        });
                        setCurrentSpousePhotoFile(null);
                        setCurrentSpousePhotoPreview(null);
                      }}
                      disabled={currentSpouseSubmitting}
                      className="btn btn--outline"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateCurrentSpouse}
                      disabled={currentSpouseSubmitting}
                      className="btn btn--primary"
                    >
                      {currentSpouseSubmitting ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Saving...
                        </>
                      ) : editingCurrentSpouseId ? (
                        "Update Current Spouse"
                      ) : (
                        "Save Current Spouse"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Application Modal */}
          {showEditModal && application && (
            <div className="modal-overlay modal-overlay--side-panel">
              <div className="modal-content modal-content--side-panel">
                <div className="modal-header">
                  <h2 className="modal-title">Edit Marriage Application</h2>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => {
                      setShowEditModal(false);
                      resetEditForm();
                    }}
                    disabled={editLoading}
                  >
                    ✕
                  </button>
                </div>

                <form className="modal-form" onSubmit={handleUpdate}>
                  {editError && (
                    <div className="alert alert-error">
                      <span className="alert-icon">⚠️</span>
                      <span>{editError}</span>
                      <button
                        type="button"
                        onClick={() => setEditError("")}
                        className="alert-close"
                      >
                        ✕
                      </button>
                    </div>
                  )}

                  {/* Form No Field - Full Width */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    <div className="form-group has-value">
                      <input
                        type="text"
                        className="form-input"
                        value={application.formNo || ""}
                        disabled
                        placeholder=" "
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      />
                      <label className="form-label">Form No</label>
                    </div>
                  </div>

                  <div className="form-grid-responsive">
                    <div className="switch-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={editFormData.processAtHeadquarters || false}
                          disabled={isRoleCountyLinked}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              processAtHeadquarters: e.target.checked,
                              countyId: e.target.checked
                                ? CountyEnum.CentralOffice
                                : linkedCountyId || 0,
                            })
                          }
                        />
                        <span className="switch-slider"></span>
                      </label>
                      <span className="switch-label">
                        Process at Headquarters
                      </span>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.countyId || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            countyId: parseInt(e.target.value, 10),
                          })
                        }
                        required
                        disabled={
                          editFormData.processAtHeadquarters ||
                          isRoleCountyLinked
                        }
                        style={
                          editFormData.processAtHeadquarters ||
                          isRoleCountyLinked
                            ? { background: "#f1f1f1", cursor: "not-allowed" }
                            : {}
                        }
                      >
                        <option value="">Select County</option>
                        {editFormData.processAtHeadquarters
                          ? counties
                              .filter(
                                (county) =>
                                  county.id === CountyEnum.CentralOffice,
                              )
                              .map((county) => (
                                <option key={county.id} value={county.id}>
                                  {county.name}
                                </option>
                              ))
                          : counties.map((county) => (
                              <option key={county.id} value={county.id}>
                                {county.name}
                              </option>
                            ))}
                      </select>
                      <label className="form-label">
                        County <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.category || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            category: e.target.value,
                          })
                        }
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        <option value={ApplicationCategory.Marriage}>
                          {ApplicationCategory.Marriage}
                        </option>
                        <option value={ApplicationCategory.Divorce}>
                          {ApplicationCategory.Divorce}
                        </option>
                      </select>
                      <label className="form-label">
                        Application Category <span className="required">*</span>
                      </label>
                    </div>

                    <div className="form-group has-value">
                      <select
                        className="form-input"
                        value={editFormData.workflowId || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            workflowId: e.target.value
                              ? parseInt(e.target.value, 10)
                              : undefined,
                          })
                        }
                        disabled
                        style={{
                          background: "#f1f1f1",
                          cursor: "not-allowed",
                        }}
                      >
                        {workflows.map((workflow) => (
                          <option key={workflow.id} value={workflow.id}>
                            {workflow.name}
                          </option>
                        ))}
                      </select>
                      <label className="form-label">Workflow</label>
                    </div>
                  </div>

                  {/* 3-Column Row for Payment Fields */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    {/* Readonly Currency field */}
                    <div
                      className={`form-group ${editServiceCharge?.currencyName ? "has-value" : ""}`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editServiceCharge?.currencyName ?? ""}
                        readOnly
                        placeholder=" "
                        style={{ background: "#f1f1f1", cursor: "not-allowed" }}
                      />
                      <label className="form-label">Currency</label>
                    </div>

                    {/* Readonly LRA Payment field */}
                    {editServiceCharge?.lraPayment != null && (
                      <div
                        className={`form-group ${editServiceCharge?.lraPayment != null ? "has-value" : ""}`}
                      >
                        <input
                          type="number"
                          className="form-input"
                          value={editServiceCharge?.lraPayment ?? ""}
                          readOnly
                          placeholder=" "
                          style={{
                            background: "#f1f1f1",
                            cursor: "not-allowed",
                          }}
                        />
                        <label className="form-label">LRA Payment</label>
                      </div>
                    )}

                    {/* Readonly Operational Cost field */}
                    {editServiceCharge?.operationalCost != null && (
                      <div
                        className={`form-group ${editServiceCharge?.operationalCost != null ? "has-value" : ""}`}
                      >
                        <input
                          type="number"
                          className="form-input"
                          value={editServiceCharge?.operationalCost ?? ""}
                          readOnly
                          placeholder=" "
                          style={{
                            background: "#f1f1f1",
                            cursor: "not-allowed",
                          }}
                        />
                        <label className="form-label">Operational Cost</label>
                      </div>
                    )}
                  </div>

                  {/* 2-Column Row for Date of Marriage and Email */}
                  <div className="form-grid-responsive">
                    <div className="form-group has-value">
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfWedding || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfWedding: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Date of Marriage <span className="required">*</span>
                      </label>
                    </div>

                    <div
                      className={`form-group ${
                        editFormData.email ? "has-value" : ""
                      }`}
                    >
                      <input
                        type="email"
                        className="form-input"
                        value={editFormData.email || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            email: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">Email</label>
                    </div>
                  </div>

                  {/* Place of Marriage Field - Full Width */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: "1rem",
                      width: "100%",
                      marginBottom: "1rem",
                    }}
                  >
                    <div
                      className={`form-group ${
                        editFormData.placeOfWedding ? "has-value" : ""
                      }`}
                    >
                      <input
                        type="text"
                        className="form-input"
                        value={editFormData.placeOfWedding || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            placeOfWedding: e.target.value,
                          })
                        }
                        placeholder=" "
                        required
                      />
                      <label className="form-label">
                        Place of Marriage <span className="required">*</span>
                      </label>
                    </div>
                  </div>

                  {/* Date of Certification - Optional field */}
                  <div className="form-grid-responsive">
                    <div className="form-group has-value">
                      <input
                        type="date"
                        className="form-input"
                        value={editFormData.dateOfCertification || ""}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            dateOfCertification: e.target.value,
                          })
                        }
                        placeholder=" "
                      />
                      <label className="form-label">
                        Date of Certification
                      </label>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn--outline"
                      onClick={() => {
                        setShowEditModal(false);
                        resetEditForm();
                      }}
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn--primary"
                      disabled={editLoading}
                    >
                      {editLoading ? (
                        <>
                          <span className="spinner spinner--sm"></span>
                          Updating...
                        </>
                      ) : (
                        "Update Application"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Manual Payment Modal */}
          {showManualPaymentModal && application && (
            <ManualPayment
              application={application}
              onClose={() => setShowManualPaymentModal(false)}
              onPaymentSuccess={() => fetchData()}
            />
          )}

          {/* Signature Extraction Modal */}
          {showSignatureExtractionModal && application && signedCertificate && (
            <SignatureExtraction
              application={application}
              documentPath={signedCertificate.documentPath}
              onClose={() => {
                setShowSignatureExtractionModal(false);
                fetchCertificateSignatures();
              }}
            />
          )}

          {/* Card Payment Modal */}
          {showCardPaymentModal && application && (
            <CardPayment
              application={application}
              onClose={() => setShowCardPaymentModal(false)}
              onPaymentSuccess={() => fetchData()}
            />
          )}
        </div>
      </main>
      {!isPrintPage && !isPortal && <Footer />}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .dashboard-body {
            margin: 0 !important;
            padding: 0 !important;
          }
          .main-content {
            margin: 0 !important;
            padding: 0 !important;
          }
          .user-detail-page {
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WeddingApplicationDetail;
